import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { Printer, History, Calendar, X } from "lucide-react";
import Layout from "./layout";
import TemplateDetailModal from "../Modal/TemplateDetailModal";
import EditTemplateModal from "../Modal/editTamplateModal";
import HealthReportModal from "../Modal/HealthReportModal";
import HealthRecordsHistory from "./HealthRecordsHistory";
import { useNavigate } from "react-router-dom";
import {
  healthRecordsAPI,
  templateAPI,
  healthRecommendationAPI,
  memberAPI,
  referenceDataAPI,
  campaignAPI,
} from "../services/api";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { createGlobalStyle } from "styled-components";

const DatePickerStyles = createGlobalStyle`
  .react-datepicker {
    font-family: inherit;
    border: 2px solid #00B5B8;
    border-radius: 0.5rem;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  }

  .react-datepicker__header {
    background-color: #00B5B8;
    border-bottom: none;
    border-radius: 0.5rem 0.5rem 0 0;
    padding: 1rem 0.75rem 0.75rem;
  }

  .react-datepicker__current-month {
    color: #FFFFFF;
    font-size: 1rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
  }

  .react-datepicker__day-name {
    color: #FFFFFF;
    font-size: 0.875rem;
    font-weight: 600;
    width: 2rem;
    line-height: 2rem;
    margin: 0.166rem;
  }

  .react-datepicker__day {
    color: #1F2937;
    font-size: 0.875rem;
    font-weight: 500;
    border-radius: 0.375rem;
    width: 2rem;
    line-height: 2rem;
    margin: 0.166rem;
    transition: all 0.2s;

    &:hover {
      background-color: #00B5B8;
      color: white;
      transform: scale(1.1);
    }
  }

  .react-datepicker__day--selected,
  .react-datepicker__day--keyboard-selected {
    background-color: #00B5B8;
    color: white;
    font-weight: 700;
    border: 2px solid #00B5B8;

    &:hover {
      background-color: #008F92;
      border-color: #008F92;
    }
  }

  .react-datepicker__day--disabled {
    color: #D1D5DB;
    cursor: not-allowed;
    opacity: 0.5;

    &:hover {
      background-color: transparent;
      transform: none;
    }
  }

  .react-datepicker__day--today {
    font-weight: 700;
    color: #00B5B8;
    background-color: #E0F2F3;
    border: 2px solid #00B5B8;

    &:hover {
      background-color: #00B5B8;
      color: white;
    }
  }

  .react-datepicker__day--outside-month {
    color: #9CA3AF;
    opacity: 0.5;
  }

  .react-datepicker__navigation {
    top: 1rem;
  }
`;

const PageContainer = styled.div`
  background-color: #ffffff;
  border-radius: 0.5rem;
  border: 1px solid #e5e7eb;
  padding: 2rem;
  margin-bottom: 2rem;
`;

const PageTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 400;
  color: #111827;
  margin-bottom: 2rem;
`;

const FilterSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr 1fr auto auto;
  grid-template-rows: repeat(2, auto);
  gap: 1rem;
  margin-bottom: 2rem;
  align-items: end;
`;

const GridItem = styled.div`
  grid-column: ${(props) => props.column};
  grid-row: ${(props) => props.row};
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  display: block;
  margin-bottom: 0.5rem;
`;

const DateInputContainer = styled.div`
  position: relative;
`;

const DateInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  padding-right: 2.5rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  color: #374151;

  &:focus {
    outline: none;
    border-color: #00b5b8;
    box-shadow: 0 0 0 3px rgba(0, 181, 184, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }

  &::-webkit-calendar-picker-indicator {
    opacity: 0;
    position: absolute;
    right: 0;
    width: 100%;
    height: 100%;
    cursor: pointer;
  }
`;

const CalendarIcon = styled.div`
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  pointer-events: none;
`;

const TextInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;

  &::placeholder {
    color: #9ca3af;
  }

  &:focus {
    outline: none;
    border-color: #00b5b8;
    box-shadow: 0 0 0 3px rgba(0, 181, 184, 0.1);
  }
`;

const SelectInput = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  background-color: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #00b5b8;
    box-shadow: 0 0 0 3px rgba(0, 181, 184, 0.1);
  }
`;

const SearchButton = styled.button`
  background-color: #00b5b8;
  color: white;
  border: none;
  border-radius: 0.375rem;
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  width: 100%;
  height: 46px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: #0891b2;
  }
`;

const PrintButton = styled.button`
  background-color: #f9fafb;
  color: #374151;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  width: 100%;
  height: 46px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;

  &:hover {
    background-color: #f3f4f6;
  }
`;

const TemplateBadge = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: ${(props) => (props.disabled ? "#F9FAFB" : "transparent")};
  border: 1px solid ${(props) => (props.disabled ? "#D1D5DB" : "#00B5B8")};
  color: ${(props) => (props.disabled ? "#9CA3AF" : "#00B5B8")};
  border-radius: 0px;
  padding: 0.25rem 0.5rem;
  margin: 0.125rem;
  font-size: 0.75rem;
  min-height: 30px;
  height: auto;
  width: auto;
  cursor: pointer;
  opacity: ${(props) => (props.disabled ? 0.6 : 1)};
`;

const TemplateBadgeText = styled.span``;

const TagBadge = styled.div`
  display: inline-flex;
  align-items: center;
  background-color: #d1fae5;
  color: #065f46;
  border: 1px solid #6ee7b7;
  border-radius: 0.375rem;
  padding: 0.25rem 0.75rem;
  margin: 0.125rem;
  font-size: 0.75rem;
  height: auto;
  width: auto;
  font-weight: 500;
`;

const ComparisonBadge = styled.div`
  font-size: 12px;
  font-weight: 500;
  text-align: center;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  display: inline-block;
  background-color: transparent;
  color: ${(props) => {
    if (props.status === "high") return "#DC2626";
    if (props.status === "low") return "#D97706";
    return "#059669";
  }};
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-top: 1px solid #e5e7eb;
`;

const PaginationInfo = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
`;

const PaginationControls = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const PageButton = styled.button`
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  background-color: ${(props) => (props.active ? "#00B5B8" : "#FFFFFF")};
  color: ${(props) => (props.active ? "#FFFFFF" : "#374151")};
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
  transition: all 0.2s;
  min-width: 40px;

  &:hover:not(:disabled) {
    background-color: ${(props) => (props.active ? "#0891B2" : "#F9FAFB")};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PageEllipsis = styled.span`
  padding: 0.5rem;
  color: #6b7280;
  font-size: 0.875rem;
`;

const EditButton = styled.button`
  background-color: #fbffff;
  color: #374151;
  border: 1px solid #cfd3d4;
  border-radius: 0.375rem;
  padding: 0.5rem 1rem;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  margin-bottom: 0.5rem;
  width: auto;
  white-space: nowrap;

  &:hover {
    background-color: #f3f4f6;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const ViewDataButton = styled.button`
  background-color: #fbffff;
  color: #9c9e9eff;
  border: 1px solid #cfd3d4;
  border-radius: 0.375rem;
  padding: 0.5rem 1rem;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  margin-bottom: 0.5rem;
  width: auto;
  white-space: nowrap;

  &:hover {
    background-color: #f3f4f6;
  }
`;

const StatusText = styled.div`
  font-size: 0.75rem;
  color: #374151;
  margin-bottom: 0.5rem;
  text-align: center;
`;

const StatusBadge = styled.div`
  display: inline-block;
  font-size: 14px;
  font-weight: 400;
  margin-bottom: 0.5rem;
  text-align: center;
  width: 100%;
  text-decoration: underline;
  background: none;
  border: none;
  color: ${(props) =>
    props.status === "รอการอนุมัติ"
      ? "#FF0404"
      : props.status === "อนุมัติและจัดส่งผลการตรวจสุขภาพแล้ว"
        ? "#00B5B8"
        : props.status === "มีการแก้ไขรอการอนุมัติอีกครั้ง"
          ? "#FF0404"
          : "#6F6F6F"};
`;

const ActionPrintButton = styled.button`
  background: none;
  color: ${(props) => (props.disabled ? "#9CA3AF" : "#374151")};
  border: none;
  padding: 0.5rem 1rem;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  width: 100%;
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};

  &:hover {
    color: ${(props) => (props.disabled ? "#9CA3AF" : "#00B5B8")};
  }
`;

const HistoryButton = styled.button`
  background: none;
  border: none;
  color: #6f6f6f;
  font-size: 14px;
  font-weight: 400;
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  text-decoration: underline;

  &:hover {
    color: #6f6f6f;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  gap: 1.5rem;
`;

const LoadingSpinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid #e5e7eb;
  border-top: 4px solid #00b5b8;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const LoadingText = styled.div`
  font-size: 1rem;
  color: #6b7280;
  font-weight: 500;
`;

const TableContainer = styled.div`
  overflow-x: auto;
  margin-top: 2rem;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background-color: #f9fafb;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #eee;

  &:hover {
    background-color: #f9fafb;
  }
`;

const TableHeaderCell = styled.th`
  padding: 1rem;
  font-size: 14px;
  font-weight: 400;
  color: #a3a3a3;
  text-align: center;
`;

const TableHeaderCell2 = styled.th`
  padding: 1rem;
  font-size: 14px;
  font-weight: 400;
  color: #a3a3a3;
  text-align: center;
`;

const TableCell = styled.td`
  padding: 1rem;
  font-size: 14px;
  color: #6f6f6f;
  text-align: center;
  vertical-align: top;
`;

const Checkbox = styled.input.attrs({ type: "checkbox" })`
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: #00b5b8;
`;

const HealthBook = () => {
  const defaultStartDate = new Date(2025, 0, 1); // 0 = มกราคม (เดือนนับจาก 0)
  const defaultEndDate = new Date();
  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(defaultEndDate);
  const [searchCompany, setSearchCompany] = useState("");
  const [searchText, setSearchText] = useState("");
  const [activeSearchText, setActiveSearchText] = useState(""); // ใช้สำหรับ filter เฉพาะเมื่อกดค้นหา
  const [isFetchingPages, setIsFetchingPages] = useState(false); // true ขณะกำลังโหลดหน้าข้อมูล
  const activeSearchTextRef = useRef(""); // ref สำหรับ filter ใน useEffect เพื่อหลีกเลี่ยง timing issue
  const fetchIdRef = useRef(0); // ref สำหรับ cancel fetch เก่า ป้องกัน race condition
  const hasSearchedRef = useRef(false); // ref สำหรับเช็คว่าเคย search แล้วหรือยัง
  const pendingSearchTextRef = useRef(null); // เก็บ search text ที่รอ apply หลังโหลดครบ
  const [status, setStatus] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isHealthReportModalOpen, setIsHealthReportModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [historySelectedRecord, setHistorySelectedRecord] = useState(null);
  const [isHiddenTemplatesModalOpen, setIsHiddenTemplatesModalOpen] =
    useState(false);
  const [hiddenTemplates, setHiddenTemplates] = useState([]);
  const [selectedRecordId, setSelectedRecordId] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);

  const navigate = useNavigate();

  const compareValues = (normalRange, resultValue) => {
    if (
      !normalRange ||
      !resultValue ||
      normalRange === "xxx" ||
      resultValue === "xxx" ||
      normalRange === "-" ||
      resultValue === "-"
    ) {
      return { status: "normal", label: "-" };
    }

    // ✅ ถ้า normalRange เป็น text (มีตัวอักษร แต่ไม่ใช่ format "M x-y, F x-y")
    // ต้องเช็คก่อน parseFloat เพราะ parseFloat("3+(300mg/dL)") = 3 ทำให้หลุดรอดไปเป็น Normal
    const normalRangeIsGenderFormat = /^[MF]\s/.test(normalRange.trim());
    if (/[a-zA-Z]/.test(normalRange.trim()) && !normalRangeIsGenderFormat) {
      const normalizedResult = resultValue
        .trim()
        .toLowerCase()
        .replace(/\s+/g, "");
      const normalValues = normalRange
        .split(",")
        .map((v) => v.trim().toLowerCase().replace(/\s+/g, ""));
      if (normalValues.includes(normalizedResult)) {
        return { status: "normal", label: "Normal" };
      } else {
        return { status: "high", label: "High" };
      }
    }

    const result = parseFloat(resultValue);
    if (isNaN(result)) {
      return { status: "normal", label: "-" };
    }

    if (normalRange.includes("-")) {
      const [min, max] = normalRange
        .split("-")
        .map((v) => parseFloat(v.trim()));
      if (result < min) {
        return { status: "low", label: "Low" };
      } else if (result > max) {
        return { status: "high", label: "High" };
      } else {
        return { status: "normal", label: "Normal" };
      }
    } else if (normalRange.startsWith("<")) {
      const max = parseFloat(normalRange.replace("<", "").trim());
      if (result >= max) {
        return { status: "high", label: "High" };
      } else {
        return { status: "normal", label: "Normal" };
      }
    } else if (normalRange.startsWith(">")) {
      const min = parseFloat(normalRange.replace(">", "").trim());
      if (result <= min) {
        return { status: "low", label: "Low" };
      } else {
        return { status: "normal", label: "Normal" };
      }
    }

    return { status: "normal", label: "Normal" };
  };

  const formatDateForAPI = (date) => {
    if (!date) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const formatDateStopToYYYYMMDD = (date) => {
    if (!date) return "";
    const next = new Date(date);
    next.setDate(next.getDate() + 1);
    return formatDateForAPI(next);
  };

  const handleSearch = async () => {
    const trimmedSearch = searchText.trim();

    try {
      setLoading(true);
      const filters = {};
      if (startDate) filters.dateStart = formatDateForAPI(startDate);
      if (endDate) filters.dateStop = formatDateStopToYYYYMMDD(endDate);
      if (trimmedSearch) filters.keywordName = trimmedSearch;
      if (searchCompany) filters.keywordCampaign = searchCompany;

      activeSearchTextRef.current = "";
      setActiveSearchText("");
      hasSearchedRef.current = true;

      await fetchHealthRecords(filters);
    } catch (error) {
      console.error("Error searching:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCompanyChange = async (e) => {
    const company = e.target.value;
    setSearchCompany(company);
    setHealthRecords([]); // ล้าง old data ทันที ไม่ให้แสดงข้อมูลบริษัทเก่า
    try {
      setLoading(true);
      const filters = {};
      if (startDate) filters.dateStart = formatDateForAPI(startDate);
      if (endDate) filters.dateStop = formatDateStopToYYYYMMDD(endDate);
      if (searchText.trim()) filters.keywordName = searchText.trim();
      if (company) filters.keywordCampaign = company;
      await fetchHealthRecords(filters);
    } catch (error) {
      console.error("Error filtering by company:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = (recordId) => {
    const record = healthRecords.find((r) => r.id === recordId);
    if (record) {
      setSelectedRecord(record);
      setIsHealthReportModalOpen(true);
    }
  };

  const handleCheckboxChange = (recordId) => {
    setSelectedRecords((prev) => {
      if (prev.includes(recordId)) {
        return prev.filter((id) => id !== recordId);
      } else {
        if (prev.length >= 10) {
          alert("สามารถเลือกได้สูงสุด 10 คนเท่านั้น");
          return prev;
        }
        return [...prev, recordId];
      }
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const currentPageRecords = healthRecords
        .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
        .map((record) => record.id);

      const newSelection = [
        ...new Set([...selectedRecords, ...currentPageRecords]),
      ];

      if (newSelection.length > 10) {
        alert("สามารถเลือกได้สูงสุด 10 คนเท่านั้น");
        setSelectedRecords(newSelection.slice(0, 10));
      } else {
        setSelectedRecords(newSelection);
      }
    } else {
      const currentPageRecords = healthRecords
        .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
        .map((record) => record.id);

      setSelectedRecords((prev) =>
        prev.filter((id) => !currentPageRecords.includes(id)),
      );
    }
  };

  const handleBatchPrint = () => {
    if (selectedRecords.length === 0) {
      setIsAlertModalOpen(true);
      return;
    }

    if (selectedRecords.length > 10) {
      alert("สามารถพิมพ์ได้สูงสุด 10 คนเท่านั้น");
      return;
    }

    const recordsToPrint = selectedRecords
      .map((recordId) => healthRecords.find((r) => r.id === recordId))
      .filter((record) => record !== undefined);

    if (recordsToPrint.length === 0) {
      alert("ไม่พบข้อมูลที่เลือก");
      return;
    }

    setSelectedRecord(recordsToPrint);
    setIsHealthReportModalOpen(true);
  };

  const handleViewData = (recordId) => {
    const record = healthRecords.find((r) => r.id === recordId);
    if (record) {
      setSelectedRecord(record);
      setIsHealthReportModalOpen(true);
    }
  };

  // Super Admin ไม่มีสิทธิ์ลบ template — Admin เท่านั้นจัดการได้
  const handleRemoveTemplate = async (recordId, templateIndex) => {};

  const handleViewHiddenTemplates = async (recordId) => {
    try {
      setSelectedRecordId(recordId);
      const response = await healthRecordsAPI.getHiddenTemplates(recordId);

      if (response.status === "success" && Array.isArray(response.data)) {
        setHiddenTemplates(response.data);
        setIsHiddenTemplatesModalOpen(true);
      } else {
        console.error("❌ ไม่สามารถดึงข้อมูล Templates ที่ซ่อนได้");
      }
    } catch (error) {
      console.error("❌ Error fetching hidden templates:", error);
    }
  };

  const handleRestoreTemplate = async (templateId) => {
    if (!selectedRecordId) return;

    try {
      await healthRecordsAPI.unhideTemplate(selectedRecordId, templateId);

      const currentFilters = {};
      if (startDate) currentFilters.dateStart = formatDateForAPI(startDate);
      if (endDate) currentFilters.dateStop = formatDateStopToYYYYMMDD(endDate);
      await fetchHealthRecords(currentFilters, { preservePage: true });

      const response =
        await healthRecordsAPI.getHiddenTemplates(selectedRecordId);
      if (response.status === "success" && Array.isArray(response.data)) {
        setHiddenTemplates(response.data);

        if (response.data.length === 0) {
          setIsHiddenTemplatesModalOpen(false);
        }
      }
    } catch (error) {
      console.error("❌ Error restoring template:", error);
    }
  };

  const handleTemplateClick = async (
    templateName,
    matchedTemplateData = null,
  ) => {
    try {
      let template = null;

      if (matchedTemplateData && matchedTemplateData.template_id) {
        const response = await templateAPI.getById(
          matchedTemplateData.template_id,
        );
        if (response.status === "success" && response.data) {
          template = response.data;
        }
      } else {
        const response = await templateAPI.getAll();
        if (response.status === "success" && response.data) {
          template = response.data.find(
            (t) => t.template_name === templateName,
          );
        }
      }

      if (template) {
        const isRadioParameter = (parameterName) => {
          const radioParams = [
            "Protein",
            "Urine Glucose",
            "Ketones",
            "Blood",
            "Leukocytes",
            "Nitrite",
            "Bilirubin",
            "Urobilinogen",
            "Color",
            "Clarity",
            "Color (Stool)",
            "Appearance (Stool)",
            "WBC (Stool)",
            "RBC (Stool)",
            "Parasite",
            "RBC Index",
          ];
          return radioParams.includes(parameterName);
        };

        const formattedTemplate = {
          templateName: template.template_name,
          createdBy: template.created_by,
          tags: template.tags || [],
          gender:
            template.gender === "male"
              ? "ชาย"
              : template.gender === "female"
                ? "หญิง"
                : "ทั้งสองเพศ",
          ageRange: [template.age_min, template.age_max], // ✅ ใช้ array แทน string
          selectedParameters:
            template.parameters?.map((p) => ({
              name: p.parameter_name,
              unit: p.unit || "",
              normalValue: p.normal_range || "-",
              normalRange: p.normal_range_array || [0, 100],
              allNormalRanges: p.all_normal_ranges || [],
            })) || [],
          parameterRanges:
            template.parameters?.reduce((acc, p) => {
              if (!isRadioParameter(p.parameter_name)) {
                acc[p.parameter_name] = [p.custom_min, p.custom_max];
              }
              return acc;
            }, {}) || {},
          parameterRadioValues:
            template.parameters?.reduce((acc, p) => {
              if (isRadioParameter(p.parameter_name)) {
                acc[p.parameter_name] = p.custom_value || "-";
              }
              return acc;
            }, {}) || {},
        };

        setSelectedTemplate(formattedTemplate);
        setIsModalOpen(true);
      } else {
        console.warn("Template not found:", templateName);
      }
    } catch (error) {
      console.error("Error loading template:", error);
    }
  };

  const handleEdit = (recordId) => {
    const record = healthRecords.find((r) => r.id === recordId);
    if (record) {
      setSelectedRecord(record);
      setSelectedRecordId(record.dbRecordId);
      setIsEditModalOpen(true);
    }
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedRecord(null);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTemplate(null);
  };

  const handleViewHistory = (recordId) => {
    const record = healthRecords.find((r) => r.id === recordId);
    if (record) {
      setHistorySelectedRecord(record);
      setIsHistoryModalOpen(true);
    }
  };

  const handleCloseHealthReportModal = () => {
    setIsHealthReportModalOpen(false);
    setSelectedRecord(null);
  };

  const [healthRecords, setHealthRecords] = useState([]);
  const [allHealthRecords, setAllHealthRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [masterData, setMasterData] = useState(null);
  const [companies, setCompanies] = useState([]);

  useEffect(() => {
    const loadMasterData = async () => {
      try {
        const result = await referenceDataAPI.getAllParameters();
        if (result.status === "success" && result.data) {
          setMasterData(result.data);
        }
      } catch (error) {
        console.error("Error loading master data:", error);
      }
    };

    const loadCompanies = async () => {
      try {
        const firstRes = await campaignAPI.getList(1);
        if (firstRes.result && Array.isArray(firstRes.list)) {
          let allCampaigns = [...firstRes.list];
          const pageAmount = firstRes.pageAmount || 1;
          for (let page = 2; page <= pageAmount; page++) {
            const pageRes = await campaignAPI.getList(page);
            if (pageRes.result && Array.isArray(pageRes.list)) {
              allCampaigns = allCampaigns.concat(pageRes.list);
            }
          }
          setCompanies(allCampaigns.filter((item) => item.name && item.name !== "-"));
        }
      } catch (error) {
        console.error("Error loading companies:", error);
      }
    };

    loadMasterData();
    loadCompanies();
  }, []);

  const normalizeParameterName = (paramName) => {
    const mapping = {
      "SGOT (AST)": "SGOT (AST)",
      "SGPT (ALT)": "SGPT (ALT)",
      "ALP (Alkaline Phosphatase)": "ALP",
      ALP: "ALP",
      "Uric Acid": "Uric Acid",
      BUN: "BUN, Urea",
      GGT: "GGT",
      Cholesterol: "คอเลสเตอรอลรวม",
      Triglyceride: "ไตรกลีเซอไรด์",
      CHO: "คอเลสเตอรอลรวม",
      TRI: "ไตรกลีเซอไรด์",
      FBS: "Fasting blood glucose",
      Basophil: "Basophil",
      "Urine Color": "Color",
      "Urine Appearance": "Urine Appearance",
      Appearance: "Urine Appearance",
      Clarity: "Clarity",
      "Urine pH": "pH",
      "Specific Gravity": "Specific Gravity",
      "Urine Protein": "Protein",
      Protein: "Protein",
      "Urine Glucose": "Sugar",
      Glucose: "Fasting blood glucose",
      "Glucose (FBS)": "Fasting blood glucose",
      "Urine Ketone": "Urine Ketone",
      Ketone: "Urine Ketone",
      "Urine Blood": "Blood",
      "Urine Leukocytes": "Leukocytes",
      "Urine Nitrite": "Nitrite",
      "Urine Bilirubin": "Bilirubin",
      Urobilinogen: "Urobilinogen",
      "Urine RBC": "RBC (Urine)",
      "Urine WBC": "WBC (Urine)",
      "Urine Bacteria": "Bacteria",
      Bacteria: "Bacteria",
      "Epithelial Cells": "Epithelial cell",
      Epithelial: "Epithelial cell",
      "Calcium Oxalate": "Calcium oxalate crystal",
      "Calcium oxalate": "Calcium oxalate crystal",
      "Uric Acid Crystal": "Uric acid crystal",
      "Uric acid": "Uric acid crystal",
      "Triple Phosphate": "Triple phosphate crystal",
      "Triple phosphate": "Triple phosphate crystal",
      "Calcium Carbonate": "Calcium Carbonate crystal",
      "Calcium carbonate": "Calcium Carbonate crystal",
      "Calcium Phosphate": "Calcium phosphate crystal",
      "Calcium phosphate": "Calcium phosphate crystal",
      Cystine: "Cystine crystal",
      "Cystine Crystal": "Cystine crystal",
      "Hyaline Cast": "Hyaline cast",
      "Granular Cast": "Granular cast",
      "Cellular Cast": "Cellular cast",
      "Waxy Cast": "Waxy cast",
      "RBC cast": "RBC cast",
      "Stool Color": "Color (Stool)",
      "Stool WBC": "WBC (Stool)",
      "Stool RBC": "RBC (Stool)",
      "Parasite, Ova": "Parasite",
      Parasite: "Parasite",
      Consistency: "Consistency",
      Sodium: "Sodium",
      Potassium: "Potassium",
      Chloride: "Chloride",
      Bicarbonate: "Bicarbonate",
      "Total Calcium": "Total Calcium",
      Calcium: "Total Calcium",
      "Inorganic phosphate": "Inorganic phosphate",
      Magnesium: "Magnesium",
      AFP: "AFP",
      PSA: "PSA",
      CEA: "CEA",
      "CA19-9": "CA19-9",
      CA125: "CA125",
      HBsAb: "HBsAb",
      "Anti-HBs Concentration": "Anti-HBs Concentration",
      "HCV Ab": "HCV Ab",
      "anti-HCV": "HCV Ab",
      "Free T3": "Free T3",
      "Free T4": "Free T4",
      TSH: "TSH",
      Cholinesterase: "Cholinesterase",
      "Direct bilirubin": "Direct bilirubin",
      BMI: "BMI",
      BPS: "BPS",
      BPD: "BPD",
      Pulse: "Pulse",
    };
    return mapping[paramName] || paramName;
  };

  const getNormalRangeFromMaster = (paramName, sex, age) => {
    if (!masterData) {
      return "-";
    }

    const searchName = normalizeParameterName(paramName);

    const searchNames = [searchName, `_hide_${searchName}`];

    for (const category in masterData) {
      const categoryData = masterData[category];
      for (const name of searchNames) {
        const matchingKey = Object.keys(categoryData).find(
          (key) => key.toLowerCase() === name.toLowerCase(),
        );
        if (matchingKey && categoryData[matchingKey]) {
          const ranges = categoryData[matchingKey];
          for (const range of ranges) {
            const sexMatch = range.sex === "F,M" || range.sex === sex;
            const ageMatch = age >= range.age_min && age <= range.age_max;
            if (sexMatch && ageMatch) {
              return range.normal_range;
            }
          }
        }
      }
    }
    return "-";
  };

  const getUnitFromMaster = (paramName) => {
    if (!masterData) {
      return "";
    }

    const searchName = normalizeParameterName(paramName);

    const searchNames = [searchName, `_hide_${searchName}`];

    for (const category in masterData) {
      const categoryData = masterData[category];
      for (const name of searchNames) {
        const matchingKey = Object.keys(categoryData).find(
          (key) => key.toLowerCase() === name.toLowerCase(),
        );
        if (matchingKey && categoryData[matchingKey]) {
          const ranges = categoryData[matchingKey];
          if (ranges && ranges.length > 0 && ranges[0].unit) {
            return ranges[0].unit;
          }
        }
      }
    }
    return "";
  };

  const categorizeParameters = (parameters) => {
    if (!masterData) {
      return { ทั้งหมด: parameters };
    }

    const categorized = {};
    const usedParams = new Set();

    for (const categoryName in masterData) {
      const categoryParams = masterData[categoryName];
      const paramNamesInCategory = Object.keys(categoryParams);

      categorized[categoryName] = parameters.filter((param) => {
        const isMatch = paramNamesInCategory.some((masterParamName) => {
          const cleanMasterName = masterParamName.replace(/^_hide_/, "");
          const normalizedParamName = normalizeParameterName(param.name);
          const paramLower = normalizedParamName.toLowerCase();
          const masterLower = cleanMasterName.toLowerCase();

          if (masterLower === paramLower) return true;

          // จับคู่แบบมี suffix ในวงเล็บ เช่น "ALP (Alkaline Phosphatase)" -> "ALP"
          // ข้ามกรณีวงเล็บเป็น qualifier เช่น "(Urine)", "(Stool)" เพราะเป็นคนละหมวด
          if (paramLower.includes(" (")) {
            const paramBase = paramLower.split(" (")[0].trim();
            const parenContent = paramLower.split(" (")[1] || "";
            const isSpecimenQualifier =
              parenContent.includes("urine") || parenContent.includes("stool");
            if (!isSpecimenQualifier && paramBase === masterLower) return true;
          }

          if (paramLower === "basophil" && masterLower === "basophile")
            return true;
          if (
            paramLower === "epithelial cells" &&
            masterLower === "epithelial cell"
          )
            return true;
          if (paramLower === "epithelial" && masterLower === "epithelial cell")
            return true;

          return false;
        });

        if (isMatch) usedParams.add(param.name);
        return isMatch;
      });
    }

    const unusedParams = parameters.filter(
      (param) => !usedParams.has(param.name),
    );
    if (unusedParams.length > 0) {
      categorized["อื่นๆ"] = unusedParams;
    }

    return Object.fromEntries(
      Object.entries(categorized).filter(([_, params]) => params.length > 0),
    );
  };

  const normalizeResultValue = (resultValue, normalRange) => {
    if (
      !resultValue ||
      !normalRange ||
      normalRange === "-" ||
      normalRange === "xxx"
    ) {
      return resultValue;
    }

    const cleanValue =
      typeof resultValue === "string"
        ? resultValue.replace(/,/g, "")
        : resultValue;
    const result = parseFloat(cleanValue);

    if (isNaN(result)) {
      return resultValue;
    }

    let referenceValue = null;

    if (normalRange.includes("-")) {
      const [min, max] = normalRange
        .split("-")
        .map((v) => parseFloat(v.trim()));
      referenceValue = Math.max(min, max);
    } else if (normalRange.startsWith("<")) {
      referenceValue = parseFloat(normalRange.replace("<", "").trim());
    } else if (normalRange.startsWith(">")) {
      referenceValue = parseFloat(normalRange.replace(">", "").trim());
    }

    if (!referenceValue || isNaN(referenceValue)) {
      return resultValue;
    }

    if (result > referenceValue * 100) {
      const normalized = result / 1000;
      return Number.isInteger(normalized)
        ? normalized.toString()
        : normalized.toFixed(2);
    } else if (result > referenceValue * 10) {
      const normalized = result / 100;
      return Number.isInteger(normalized)
        ? normalized.toString()
        : normalized.toFixed(2);
    }

    return resultValue;
  };

  useEffect(() => {
    if (masterData) {
      const filters = {
        dateStart: formatDateForAPI(defaultStartDate),
        dateStop: formatDateStopToYYYYMMDD(defaultEndDate),
      };
      hasSearchedRef.current = true;
      fetchHealthRecords(filters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [masterData]);

  useEffect(() => {
    const handleMemberUpdated = () => {
      // re-fetch เฉพาะถ้า user เคย search แล้วเท่านั้น
      if (!hasSearchedRef.current) return;
      const currentFilters = {};
      if (startDate) currentFilters.dateStart = formatDateForAPI(startDate);
      if (endDate) currentFilters.dateStop = formatDateStopToYYYYMMDD(endDate);
      fetchHealthRecords(currentFilters, { preservePage: true });
    };
    window.addEventListener("memberDataUpdated", handleMemberUpdated);
    return () =>
      window.removeEventListener("memberDataUpdated", handleMemberUpdated);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentPage]);

  useEffect(() => {
    if (allHealthRecords.length === 0) {
      // ถ้ายังโหลดอยู่ → คงข้อมูลเก่าไว้ไม่ให้หน้าว่าง รอ data batch แรกมาก่อน
      if (isFetchingPages) return;
      setHealthRecords([]);
      return;
    }

    // มี company filter → filter progressively ต่อ batch ที่เข้ามา (ผลสะสม ไม่กระพริบลง)

    let filtered = [...allHealthRecords];

    // ใช้ ref แทน state เพื่อให้ได้ค่าล่าสุดเสมอ แก้ timing issue กรณี useEffect fire ก่อน state commit
    // ถ้ายังโหลดอยู่ ให้รอโหลดครบก่อน (pendingSearchTextRef จะ apply เมื่อโหลดครบ)
    const currentSearchText = activeSearchTextRef.current || activeSearchText;
    if (!isFetchingPages && currentSearchText && currentSearchText.trim()) {
      const keywordInput = currentSearchText.toLowerCase().trim();
      const keywords = keywordInput.split(/\s+/).filter((k) => k.length > 0);

      if (keywords.length > 0) {
        filtered = filtered.filter((record) => {
          const patientName = (record.patientName || "").toLowerCase();
          const hn = (record.hn || "").toLowerCase();
          const searchableText = `${patientName} ${hn}`;

          return keywords.every((kw) => searchableText.includes(kw));
        });
      }
    }

    if (searchCompany) {
      filtered = filtered.filter((record) => {
        return record.company && record.company.includes(searchCompany);
      });
    }

    if (status) {
      let statusEnum = "pending";
      if (status === "อนุมัติและจัดส่งผลการตรวจสุขภาพแล้ว") {
        statusEnum = "approved";
      } else if (status === "มีการแก้ไขรอการอนุมัติอีกครั้ง") {
        statusEnum = "revised";
      }

      filtered = filtered.filter((record) => {
        return record.approvalStatus === statusEnum;
      });
    }

    setHealthRecords(filtered);
    const newTotalPages = Math.ceil(filtered.length / itemsPerPage);
    setCurrentPage((prev) => (prev > newTotalPages ? 1 : prev));
  }, [allHealthRecords, searchCompany, activeSearchText, status, isFetchingPages]);

  const matchTemplatesWithRecords = async (records, allTemplates) => {
    try {
      if (!allTemplates || allTemplates.length === 0) return;

      await Promise.all(
        records.map(async (record) => {
          let manualTemplates = null;
          const hiddenIdSet = new Set();
          const manualIdSet = new Set();

          if (record.dbRecordId) {
            const [manualResponse, hiddenResponse] = await Promise.all([
              healthRecordsAPI
                .getManualTemplates(record.dbRecordId)
                .catch(() => null),
              healthRecordsAPI
                .getHiddenTemplates(record.dbRecordId)
                .catch(() => null),
            ]);

            if (
              manualResponse?.status === "success" &&
              manualResponse.data?.length > 0
            ) {
              manualTemplates = manualResponse.data;
              manualTemplates.forEach((mt) =>
                manualIdSet.add(Number(mt.template_id)),
              );
            }

            if (
              hiddenResponse?.status === "success" &&
              Array.isArray(hiddenResponse.data)
            ) {
              hiddenResponse.data.forEach((h) =>
                hiddenIdSet.add(Number(h.template_id)),
              );
            }
          }

          const matchedTemplates = [];
          const allTags = new Set();

          const recordGender = record.gender === "ชาย" ? "male" : "female";
          const recordAge = parseInt(record.age) || 0;
          const ageKnown = record.age !== "-" && recordAge > 0;
          const recordParameterNames = new Set(
            record.parameters.map((p) => p.name),
          );

          for (const template of allTemplates) {
            if (template.is_active !== 1) continue;

            const tid = Number(template.id);

            if (hiddenIdSet.has(tid)) continue;
            if (manualIdSet.has(tid)) continue;

            const templateGender = template.gender;
            if (templateGender !== "both" && templateGender !== recordGender)
              continue;

            // ตรวจสอบ age range — ถ้าไม่รู้อายุ (โหลดแบบ bulk ไม่มี profile.age) ให้ข้าม age check
            if (ageKnown && (recordAge < template.age_min || recordAge > template.age_max))
              continue;

            const templateParams = template.parameters || [];
            let matchCount = 0;
            let totalParams = templateParams.length;

            const mediumToRecordParam = {
              คอเลสเตอรอลรวม: "Cholesterol",
              ไตรกลีเซอไรด์: "Triglyceride",
              "BUN, Urea": "BUN",
              "น้ำตาล (Sugar)": "Sugar",
              เซลล์เยื่อบุผิว: "Epithelial cell",
              "RBC (Urine)": "Urine RBC",
              "WBC (Urine)": "Urine WBC",
              "Epithelial cell": "Epithelial Cells",
            };

            const COUNT_DROPDOWN_OPTIONS = [
              "0-1",
              "1-2",
              "2-3",
              "3-5",
              "5-10",
              "10-20",
              "20-30",
              "30-50",
              "50-100",
              "100-200",
              ">200",
            ];
            const COUNT_DROPDOWN_PARAMS = [
              "RBC (Urine)",
              "WBC (Urine)",
              "Epithelial cell",
              "Red Blood Cells",
              "White Blood Cells",
              "เซลล์เยื่อบุผิว",
            ];

            for (const templateParam of templateParams) {
              const mappedName =
                mediumToRecordParam[templateParam.parameter_name] ||
                templateParam.parameter_name;
              const recordParamName = recordParameterNames.has(mappedName)
                ? mappedName
                : recordParameterNames.has(templateParam.parameter_name)
                  ? templateParam.parameter_name
                  : mappedName;
              if (recordParameterNames.has(recordParamName)) {
                const recordParam = record.parameters.find(
                  (p) => p.name === recordParamName,
                );
                if (recordParam && recordParam.result) {
                  const isCountParam = COUNT_DROPDOWN_PARAMS.includes(
                    templateParam.parameter_name,
                  );

                  if (isCountParam) {
                    const stripUnit = (v) =>
                      (v || "")
                        .replace(/cells\/hpf/i, "")
                        .replace(/\/hpf/i, "")
                        .replace(/\/lpf/i, "")
                        .replace(/cells\/µl/i, "")
                        .trim();
                    const valueIdx = COUNT_DROPDOWN_OPTIONS.indexOf(
                      stripUnit(recordParam.result),
                    );
                    const minIdx = COUNT_DROPDOWN_OPTIONS.indexOf(
                      stripUnit(templateParam.custom_min),
                    );
                    const maxIdx = COUNT_DROPDOWN_OPTIONS.indexOf(
                      stripUnit(templateParam.custom_max),
                    );

                    if (valueIdx !== -1 && minIdx !== -1 && maxIdx !== -1) {
                      if (valueIdx >= minIdx && valueIdx <= maxIdx) {
                        matchCount++;
                      }
                    }
                  } else {
                    const value = parseFloat(recordParam.result);
                    const customMin = parseFloat(templateParam.custom_min);
                    const customMax = parseFloat(templateParam.custom_max);

                    if (
                      !isNaN(value) &&
                      !isNaN(customMin) &&
                      !isNaN(customMax)
                    ) {
                      if (value >= customMin && value <= customMax) {
                        matchCount++;
                      }
                    }
                  }
                }
              }
            }

            if (totalParams > 0 && matchCount === totalParams) {
              matchedTemplates.push({
                template_id: tid,
                template_name: template.template_name,
                match_percentage: Math.round((matchCount / totalParams) * 100),
                tags: template.tags || [],
              });

              if (template.tags && Array.isArray(template.tags)) {
                template.tags.forEach((tag) => allTags.add(tag));
              }
            }
          }

          if (manualTemplates && manualTemplates.length > 0) {
            manualTemplates.forEach((mt) => {
              matchedTemplates.push(mt);
              if (mt.tags && Array.isArray(mt.tags)) {
                mt.tags.forEach((tag) => allTags.add(tag));
              }
            });
          }

          record.matchedTemplates = matchedTemplates;
          record.tags = Array.from(allTags);
          record.templates = matchedTemplates.map((mt) => mt.template_name);
        }),
      );
    } catch (error) {
      console.error("Error matching templates:", error);
    }
  };

  const fetchHealthRecords = async (
    filters = {},
    { preservePage = false } = {},
  ) => {
    const currentFetchId = ++fetchIdRef.current;

    try {
      setLoading(true);
      setIsFetchingPages(true);
      if (!preservePage) setCurrentPage(1);

      const apiFilters = { ...filters };

      // ✅ ดึง templates ทันที (รันพร้อมกับการ fetch ข้อมูล)
      let allTemplates = [];
      const templatesPromise = templateAPI
        .getAll()
        .then((res) => {
          if (res.status === "success" && res.data) allTemplates = res.data;
        })
        .catch(() => {});

      // Helper: sort และ update state ทันที (ผ่าน allHealthRecords → useEffect จัดการ filter)
      const toTimestamp = (rawDate) => {
        if (!rawDate) return 0;
        const d = new Date(rawDate);
        if (!isNaN(d.getTime())) return d.getTime();
        const parts = String(rawDate).split(/[-\/]/);
        if (parts.length === 3 && parts[0].length <= 2) {
          const d2 = new Date(parts[2], parts[1] - 1, parts[0]);
          return isNaN(d2.getTime()) ? 0 : d2.getTime();
        }
        return 0;
      };
      const sortAndUpdate = (records) => {
        if (currentFetchId !== fetchIdRef.current) return;
        const sorted = [...records].sort((a, b) => {
          const tA = toTimestamp(a.rawDate);
          const tB = toTimestamp(b.rawDate);
          if (tA !== tB) return tB - tA;
          return a.patientName.localeCompare(b.patientName, "th");
        });
        sorted.forEach((r, i) => { r.no = i + 1; });
        setAllHealthRecords([...sorted]);
      };

      // Deduplication tracking
      const seenKeys = new Set();
      const deduplicateList = (list) =>
        list.filter((r) => {
          const key = `${r.memberid}_${r.dateadd}`;
          if (seenKeys.has(key)) return false;
          seenKeys.add(key);
          return true;
        });

      let allFormattedData = [];

      // แปลงข้อมูล per batch
      const formatBatch = async (list) =>
        await Promise.all(
          list.map(async (record, index) => {
            const profile = record.profile || {};
            const dataLab = record.data_lab || [];

            const separateValueAndUnit = (value) => {
              if (!value || typeof value !== "string")
                return { value, unit: "" };

              const patterns = [
                { regex: /^(.+?)(Cells\/HPF)$/i, split: true }, // "1-2Cells/HPF" -> "1-2" + "Cells/HPF"
                { regex: /^(.+?)(\/HPF)$/i, split: true }, // "0-5/HPF" -> "0-5" + "/HPF"
                { regex: /^(.+?)(\/LPF)$/i, split: true }, // "0-2/LPF" -> "0-2" + "/LPF"
                { regex: /^(.+?)(cells\/µL)$/i, split: true }, // "100cells/µL" -> "100" + "cells/µL"
              ];

              for (const pattern of patterns) {
                const match = value.match(pattern.regex);
                if (match) {
                  return { value: match[1], unit: match[2] };
                }
              }

              return { value, unit: "" };
            };

            // API อาจส่งค่าซ้ำกัน (เช่น liverfunctiontestalk มา 3 ค่า: ผลแลป, ค่าปกติ, หน่วย) — เก็บเฉพาะค่าแรกที่ไม่ว่างเปล่า
            const labData = {};
            dataLab.forEach((item, idx) => {
              const strValue =
                item.value != null ? String(item.value).trim() : "";
              if (!labData[item.name] && strValue !== "") {
                labData[item.name] = strValue;
                const normalizedKey = item.name
                  .toLowerCase()
                  .replace(/\s+/g, "");
                if (!labData[normalizedKey]) {
                  labData[normalizedKey] = strValue;
                }
                const underscoredKey = item.name
                  .toLowerCase()
                  .replace(/\s+/g, "")
                  .replace(/-/g, "_");
                if (!labData[underscoredKey]) {
                  labData[underscoredKey] = strValue;
                }
              }
            });

            const calculateAge = (birthday) => {
              if (!birthday) return null;
              const birthDate = new Date(birthday);
              const today = new Date();

              let birthYear = birthDate.getFullYear();

              if (birthYear > 2500) {
                birthYear = birthYear - 543;
              }

              let age = today.getFullYear() - birthYear;

              const birthMonth = birthDate.getMonth();
              const birthDay = birthDate.getDate();
              const monthDiff = today.getMonth() - birthMonth;

              if (
                monthDiff < 0 ||
                (monthDiff === 0 && today.getDate() < birthDay)
              ) {
                age--;
              }

              if (age < 0 || age > 150) {
                return 0;
              }

              return age;
            };

            const birthdayField =
              profile.birthday ||
              profile.birthdate ||
              record.birthday ||
              record.birthdate ||
              profile.birth_date ||
              record.birth_date;

            let age = 0;
            if (birthdayField) {
              const calculatedAge = calculateAge(birthdayField);

              if (calculatedAge !== null && calculatedAge !== 0) {
                age = calculatedAge;
              } else if (profile.age) {
                age = parseInt(profile.age);
              }
            } else if (profile.age) {
              age = parseInt(profile.age);
            }
            const sex = profile.gender === "1" ? "M" : "F";

            const parameters = [];
            const testsNeedingNormalRange = [];

            // CBC - Complete Blood Count
            if (labData["wbc"] || labData["cbcwbcresult"]) {
              const param = {
                name: "WBC",
                result: labData["wbc"] || labData["cbcwbcresult"],
                normal: null,
                unit: getUnitFromMaster("WBC"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({ param, name: "WBC", sex, age });
            }

            if (labData["hb"] || labData["cbchbresult"]) {
              const param = {
                name: "Hemoglobin",
                result: labData["hb"] || labData["cbchbresult"],
                normal: null,
                unit: getUnitFromMaster("Hemoglobin"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Hemoglobin",
                sex,
                age,
              });
            }

            if (labData["hct"] || labData["cbchctresult"]) {
              const param = {
                name: "Hematocrit",
                result: labData["hct"] || labData["cbchctresult"],
                normal: null,
                unit: getUnitFromMaster("Hematocrit"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Hematocrit",
                sex,
                age,
              });
            }

            if (labData["mcv"] || labData["cbcmcvresult"]) {
              const param = {
                name: "MCV",
                result: labData["mcv"] || labData["cbcmcvresult"],
                normal: null,
                unit: getUnitFromMaster("MCV"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({ param, name: "MCV", sex, age });
            }

            if (labData["mch"] || labData["cbcmchresult"]) {
              const param = {
                name: "MCH",
                result: labData["mch"] || labData["cbcmchresult"],
                normal: null,
                unit: getUnitFromMaster("MCH"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({ param, name: "MCH", sex, age });
            }

            if (labData["mchc"] || labData["cbcmchcresult"]) {
              const param = {
                name: "MCHC",
                result: labData["mchc"] || labData["cbcmchcresult"],
                normal: null,
                unit: getUnitFromMaster("MCHC"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({ param, name: "MCHC", sex, age });
            }

            if (labData["plt"] || labData["cbcpltcountresult"]) {
              const param = {
                name: "Platelets",
                result: labData["plt"] || labData["cbcpltcountresult"],
                normal: null,
                unit: getUnitFromMaster("Platelets"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Platelets",
                sex,
                age,
              });
            }

            if (labData["neutrophile"] || labData["cbcneutrophileresult"]) {
              const param = {
                name: "Neutrophile",
                result:
                  labData["neutrophile"] || labData["cbcneutrophileresult"],
                normal: null,
                unit: getUnitFromMaster("Neutrophile"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Neutrophile",
                sex,
                age,
              });
            }

            if (labData["lymphocyte_count"] || labData["cbclymphocyteresult"]) {
              const param = {
                name: "Lymphocyte",
                result:
                  labData["lymphocyte_count"] || labData["cbclymphocyteresult"],
                normal: null,
                unit: getUnitFromMaster("Lymphocyte"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Lymphocyte",
                sex,
                age,
              });
            }

            if (labData["monocyte_count"] || labData["cbcmonocyteresult"]) {
              const param = {
                name: "Monocyte",
                result:
                  labData["monocyte_count"] || labData["cbcmonocyteresult"],
                normal: null,
                unit: getUnitFromMaster("Monocyte"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Monocyte",
                sex,
                age,
              });
            }

            if (labData["cbceosinophileresult"]) {
              const param = {
                name: "Eosinophil",
                result: labData["cbceosinophileresult"],
                normal: null,
                unit: getUnitFromMaster("Eosinophil"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Eosinophil",
                sex,
                age,
              });
            }

            // Kidney Function Tests
            if (
              labData["creatinine"] ||
              labData["creatininecreatinineresult"]
            ) {
              const param = {
                name: "Creatinine",
                result:
                  labData["creatinine"] ||
                  labData["creatininecreatinineresult"],
                normal: null,
                unit: getUnitFromMaster("Creatinine"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Creatinine",
                sex,
                age,
              });
            }

            if (labData["creatinineegfrresult"] || labData["egfr"]) {
              const param = {
                name: "eGFR",
                result: labData["creatinineegfrresult"] || labData["egfr"],
                normal: null,
                unit: getUnitFromMaster("eGFR"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({ param, name: "eGFR", sex, age });
            }

            if (labData["uric"] || labData["uricacidresult"]) {
              const param = {
                name: "Uric Acid",
                result: labData["uric"] || labData["uricacidresult"],
                normal: null,
                unit: getUnitFromMaster("Uric Acid"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Uric Acid",
                sex,
                age,
              });
            }

            // Diabetes Tests
            if (
              labData["glucoseresult"] ||
              labData["fbs"] ||
              labData["fbs/fpg"]
            ) {
              const param = {
                name: "Fasting blood glucose",
                result:
                  labData["glucoseresult"] ||
                  labData["fbs"] ||
                  labData["fbs/fpg"],
                normal: null,
                unit: getUnitFromMaster("Fasting blood glucose"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Fasting blood glucose",
                sex,
                age,
              });
            }

            if (labData["non_npo_glucose"] || labData["sugar_non_npo"]) {
              const param = {
                name: "Non-NPO blood glucose",
                result: labData["non_npo_glucose"] || labData["sugar_non_npo"],
                normal: null,
                unit: getUnitFromMaster("Non-NPO blood glucose"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Non-NPO blood glucose",
                sex,
                age,
              });
            }

            if (labData["hba1c"] || labData["hba1chba1cresult"]) {
              const param = {
                name: "HbA1C",
                result: labData["hba1c"] || labData["hba1chba1cresult"],
                normal: null,
                unit: getUnitFromMaster("HbA1C"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({ param, name: "HbA1C", sex, age });
            }

            // Liver Function Tests
            if (labData["sgot"] || labData["liverfunctiontestsgotastresult"]) {
              const param = {
                name: "SGOT (AST)",
                result:
                  labData["sgot"] || labData["liverfunctiontestsgotastresult"],
                normal: null,
                unit: getUnitFromMaster("SGOT (AST)"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "SGOT (AST)",
                sex,
                age,
              });
            }

            if (
              labData["sgptresult"] ||
              labData["sgpt"] ||
              labData["liverfunctiontestsgotaltresult"]
            ) {
              const param = {
                name: "SGPT (ALT)",
                result:
                  labData["sgptresult"] ||
                  labData["sgpt"] ||
                  labData["liverfunctiontestsgotaltresult"],
                normal: null,
                unit: getUnitFromMaster("SGPT (ALT)"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "SGPT (ALT)",
                sex,
                age,
              });
            }

            if (
              labData["alp"] ||
              labData["liverfunctiontestalkalinephosphataseresult"]
            ) {
              const param = {
                name: "ALP (Alkaline Phosphatase)",
                result:
                  labData["alp"] ||
                  labData["liverfunctiontestalkalinephosphataseresult"],
                normal: null,
                unit: getUnitFromMaster("ALP (Alkaline Phosphatase)"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({ param, name: "ALP", sex, age });
            }

            if (labData["bun"] || labData["bunresult"]) {
              const param = {
                name: "BUN",
                result: labData["bun"] || labData["bunresult"],
                normal: null,
                unit: getUnitFromMaster("BUN"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "BUN, Urea",
                sex,
                age,
              });
            }

            if (
              labData["totalbilirubin"] ||
              labData["total_bilirubin"] ||
              labData["bilirubin"] ||
              labData["liverfunctiontestbilirubintotalresult"]
            ) {
              const param = {
                name: "Total Bilirubin",
                result:
                  labData["totalbilirubin"] ||
                  labData["total_bilirubin"] ||
                  labData["bilirubin"] ||
                  labData["liverfunctiontestbilirubintotalresult"],
                normal: null,
                unit: getUnitFromMaster("Total Bilirubin"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Total Bilirubin",
                sex,
                age,
              });
            }

            if (
              labData["direct_bilirubin"] ||
              labData["directbilirubin"] ||
              labData["liverfunctiontestbilirubindirectresult"]
            ) {
              const param = {
                name: "Direct bilirubin",
                result:
                  labData["direct_bilirubin"] ||
                  labData["directbilirubin"] ||
                  labData["liverfunctiontestbilirubindirectresult"],
                normal: null,
                unit: getUnitFromMaster("Direct bilirubin"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Direct bilirubin",
                sex,
                age,
              });
            }

            if (
              labData["albumin"] ||
              labData["liverfunctiontestalbuminresult"]
            ) {
              const param = {
                name: "Albumin",
                result:
                  labData["albumin"] ||
                  labData["liverfunctiontestalbuminresult"],
                normal: null,
                unit: getUnitFromMaster("Albumin"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Albumin",
                sex,
                age,
              });
            }

            if (
              labData["totalprotein"] ||
              labData["total_protein"] ||
              labData["liverfunctiontesttotalproteinresult"]
            ) {
              const param = {
                name: "Total Protein",
                result:
                  labData["totalprotein"] ||
                  labData["total_protein"] ||
                  labData["liverfunctiontesttotalproteinresult"],
                normal: null,
                unit: getUnitFromMaster("Total Protein"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Total Protein",
                sex,
                age,
              });
            }

            if (labData["ggt"]) {
              const param = {
                name: "GGT",
                result: labData["ggt"],
                normal: null,
                unit: getUnitFromMaster("GGT"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({ param, name: "GGT", sex, age });
            }

            if (
              labData["globulin"] ||
              labData["liverfunctiontestglobulinresult"]
            ) {
              const param = {
                name: "Globulin",
                result:
                  labData["globulin"] ||
                  labData["liverfunctiontestglobulinresult"],
                normal: null,
                unit: getUnitFromMaster("Globulin"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Globulin",
                sex,
                age,
              });
            }

            // Lipid Profile
            if (labData["hdl"] || labData["hdlcholesterolresult"]) {
              const param = {
                name: "HDL",
                result: labData["hdl"] || labData["hdlcholesterolresult"],
                normal: null,
                unit: getUnitFromMaster("HDL"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({ param, name: "HDL", sex, age });
            }

            if (labData["ldl"] || labData["ldlcholesterolresult"]) {
              const param = {
                name: "LDL",
                result: labData["ldl"] || labData["ldlcholesterolresult"],
                normal: null,
                unit: getUnitFromMaster("LDL"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({ param, name: "LDL", sex, age });
            }

            if (
              labData["chol"] ||
              labData["cholesterol"] ||
              labData["cho"] ||
              labData["cholesterolresult"]
            ) {
              const param = {
                name: "Cholesterol",
                result:
                  labData["chol"] ||
                  labData["cholesterol"] ||
                  labData["cho"] ||
                  labData["cholesterolresult"],
                normal: null,
                unit: getUnitFromMaster("Cholesterol"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "คอเลสเตอรอลรวม",
                sex,
                age,
              });
            }

            const tgResultKey = Object.keys(labData).find(
              (k) => k.includes("triglyceride") && k.includes("result"),
            );
            const tgAnyKey = Object.keys(labData).find(
              (k) =>
                k.includes("triglyceride") &&
                !k.includes("normal") &&
                !k.includes("unit"),
            );
            const tgValue =
              labData["triglycerideresult"] ||
              labData["lipidprofiletriglycerideresult"] ||
              (tgResultKey ? labData[tgResultKey] : null) ||
              labData["lipidprofiletriglyceride"] ||
              labData["triglyceride"] ||
              labData["tg"] ||
              labData["tri"] ||
              (tgAnyKey ? labData[tgAnyKey] : null);

            if (
              labData["triglyceride"] ||
              labData["tri"] ||
              labData["tg"] ||
              labData["triglycerideresult"] ||
              labData["lipidprofiletriglyceride"] ||
              labData["lipidprofiletriglycerideresult"] ||
              tgResultKey ||
              tgAnyKey
            ) {
              const param = {
                name: "Triglyceride",
                result: tgValue,
                normal: null,
                unit: getUnitFromMaster("Triglyceride"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "ไตรกลีเซอไรด์",
                sex,
                age,
              });
            }

            // CBC Additional Parameters
            if (labData["rbc"]) {
              const param = {
                name: "RBC",
                result: labData["rbc"],
                normal: null,
                unit: getUnitFromMaster("RBC"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({ param, name: "RBC", sex, age });
            }

            if (labData["neutrophile_percent"] || labData["neutrophile%"]) {
              const param = {
                name: "Neutrophile %",
                result:
                  labData["neutrophile_percent"] || labData["neutrophile%"],
                normal: null,
                unit: getUnitFromMaster("Neutrophile %"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Neutrophile %",
                sex,
                age,
              });
            }

            if (labData["lymphocyte_percent"] || labData["lymphocyte%"]) {
              const param = {
                name: "Lymphocyte %",
                result: labData["lymphocyte_percent"] || labData["lymphocyte%"],
                normal: null,
                unit: getUnitFromMaster("Lymphocyte %"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Lymphocyte %",
                sex,
                age,
              });
            }

            if (labData["monocyte_percent"] || labData["monocyte%"]) {
              const param = {
                name: "Monocyte %",
                result: labData["monocyte_percent"] || labData["monocyte%"],
                normal: null,
                unit: getUnitFromMaster("Monocyte %"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Monocyte %",
                sex,
                age,
              });
            }

            if (
              labData["eosinophil_percent"] ||
              labData["eosinophile_percent"] ||
              labData["eosinophil%"] ||
              labData["eosinophile%"] ||
              labData["eosinophile"]
            ) {
              const param = {
                name: "Eosinophil %",
                result:
                  labData["eosinophil_percent"] ||
                  labData["eosinophile_percent"] ||
                  labData["eosinophil%"] ||
                  labData["eosinophile%"] ||
                  labData["eosinophile"],
                normal: null,
                unit: getUnitFromMaster("Eosinophil %"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Eosinophil %",
                sex,
                age,
              });
            }

            if (
              labData["basophil_percent"] ||
              labData["basophil%"] ||
              labData["basophile"]
            ) {
              const param = {
                name: "Basophil %",
                result:
                  labData["basophil_percent"] ||
                  labData["basophil%"] ||
                  labData["basophile"],
                normal: null,
                unit: getUnitFromMaster("Basophil %"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Basophil %",
                sex,
                age,
              });
            }

            // Urinalysis - Complete Panel
            if (
              labData["urinalysissugarresult"] ||
              labData["urinalysissugarresul"] ||
              labData["urine_glucose"]
            ) {
              const param = {
                name: "Urine Glucose",
                result:
                  labData["urinalysissugarresult"] ||
                  labData["urinalysissugarresul"] ||
                  labData["urine_glucose"],
                normal: null,
                unit: getUnitFromMaster("Urine Glucose"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Urine Glucose",
                sex,
                age,
              });
            }

            if (
              labData["urinalysisproteinresult"] ||
              labData["urinalysisproteinres"] ||
              labData["urine_protein"]
            ) {
              const param = {
                name: "Urine Protein",
                result:
                  labData["urinalysisproteinresult"] ||
                  labData["urinalysisproteinres"] ||
                  labData["urine_protein"],
                normal: null,
                unit: getUnitFromMaster("Urine Protein"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Protein",
                sex,
                age,
              });
            }

            if (
              labData["urinalysiscolorresult"] ||
              labData["urinalysiscolorresul"] ||
              labData["urine_color"] ||
              labData["color"]
            ) {
              const param = {
                name: "Urine Color",
                result:
                  labData["urinalysiscolorresult"] ||
                  labData["urinalysiscolorresul"] ||
                  labData["urine_color"] ||
                  labData["color"],
                normal: null,
                unit: getUnitFromMaster("Urine Color"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({ param, name: "Color", sex, age });
            }

            if (labData["urinalysisclarityresult"] || labData["clarity"]) {
              const param = {
                name: "Clarity",
                result:
                  labData["urinalysisclarityresult"] || labData["clarity"],
                normal: null,
                unit: getUnitFromMaster("Clarity"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Clarity",
                sex,
                age,
              });
            }

            if (
              labData["urinalysisapperanceresult"] ||
              labData["urinalysisapperancer"] ||
              labData["urine_clarity"]
            ) {
              const param = {
                name: "Urine Appearance",
                result:
                  labData["urinalysisapperanceresult"] ||
                  labData["urinalysisapperancer"] ||
                  labData["urine_clarity"],
                normal: null,
                unit: getUnitFromMaster("Urine Appearance"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Appearance",
                sex,
                age,
              });
            }

            if (
              labData["urinalysisphresult"] ||
              labData["urine_ph"] ||
              labData["ph"]
            ) {
              const param = {
                name: "Urine pH",
                result:
                  labData["urinalysisphresult"] ||
                  labData["urine_ph"] ||
                  labData["ph"],
                normal: null,
                unit: getUnitFromMaster("Urine pH"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({ param, name: "pH", sex, age });
            }

            if (
              labData["urinalysisspecificgravityresult"] ||
              labData["urinalysisspecificgr"] ||
              labData["urine_specific_gravity"] ||
              labData["specific_gravity"]
            ) {
              const param = {
                name: "Specific Gravity",
                result:
                  labData["urinalysisspecificgravityresult"] ||
                  labData["urinalysisspecificgr"] ||
                  labData["urine_specific_gravity"] ||
                  labData["specific_gravity"],
                normal: null,
                unit: getUnitFromMaster("Specific Gravity"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Specific Gravity",
                sex,
                age,
              });
            }

            if (
              labData["urinalysisketoneresult"] ||
              labData["urinalysisketoneresu"] ||
              labData["urine_ketones"] ||
              labData["ketones"]
            ) {
              const param = {
                name: "Urine Ketone",
                result:
                  labData["urinalysisketoneresult"] ||
                  labData["urinalysisketoneresu"] ||
                  labData["urine_ketones"] ||
                  labData["ketones"],
                normal: null,
                unit: getUnitFromMaster("Urine Ketone"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({ param, name: "Ketone", sex, age });
            }

            if (
              labData["urinalysisbloodresult"] ||
              labData["urinalysisbloodresul"] ||
              labData["urine_blood"] ||
              labData["blood"]
            ) {
              const param = {
                name: "Urine Blood",
                result:
                  labData["urinalysisbloodresult"] ||
                  labData["urinalysisbloodresul"] ||
                  labData["urine_blood"] ||
                  labData["blood"],
                normal: null,
                unit: getUnitFromMaster("Urine Blood"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({ param, name: "Blood", sex, age });
            }

            if (
              labData["urinalysisrbcresult"] ||
              labData["urinalysisrbc"] ||
              labData["urine_rbc"] ||
              labData["rbc_urine"]
            ) {
              const { value, unit } = separateValueAndUnit(
                labData["urinalysisrbcresult"] ||
                  labData["urinalysisrbc"] ||
                  labData["urine_rbc"] ||
                  labData["rbc_urine"],
              );
              const param = {
                name: "Urine RBC",
                result: value,
                normal: null,
                unit: unit,
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Urine RBC",
                sex,
                age,
              });
            }

            if (
              labData["urinalysiswbcresult"] ||
              labData["urinalysiswbc"] ||
              labData["urine_wbc"] ||
              labData["wbc_urine"]
            ) {
              const { value, unit } = separateValueAndUnit(
                labData["urinalysiswbcresult"] ||
                  labData["urinalysiswbc"] ||
                  labData["urine_wbc"] ||
                  labData["wbc_urine"],
              );
              const param = {
                name: "Urine WBC",
                result: value,
                normal: null,
                unit: unit,
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Urine WBC",
                sex,
                age,
              });
            }

            if (
              labData["urinalysisbactcellresult"] ||
              labData["urinalysisbactcellre"]
            ) {
              const { value, unit } = separateValueAndUnit(
                labData["urinalysisbactcellresult"] ||
                  labData["urinalysisbactcellre"],
              );
              const param = {
                name: "Urine Bacteria",
                result: value,
                normal: null,
                unit: unit,
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Bacteria",
                sex,
                age,
              });
            }

            if (
              labData["urinalysisepithelialcellresult"] ||
              labData["urinalysisepithelial"]
            ) {
              const { value, unit } = separateValueAndUnit(
                labData["urinalysisepithelialcellresult"] ||
                  labData["urinalysisepithelial"],
              );
              const param = {
                name: "Epithelial Cells",
                result: value,
                normal: null,
                unit: unit,
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Epithelial",
                sex,
                age,
              });
            }

            if (
              labData["urinalysisleukocyte"] ||
              labData["urine_leukocyte"] ||
              labData["urine_leukocytes"] ||
              labData["leukocytes"]
            ) {
              const param = {
                name: "Urine Leukocytes",
                result:
                  labData["urinalysisleukocyte"] ||
                  labData["urine_leukocyte"] ||
                  labData["urine_leukocytes"] ||
                  labData["leukocytes"],
                normal: null,
                unit: getUnitFromMaster("Urine Leukocytes"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Leukocytes",
                sex,
                age,
              });
            }

            if (
              labData["urinalysisnitrite"] ||
              labData["urine_nitrite"] ||
              labData["nitrite"]
            ) {
              const param = {
                name: "Urine Nitrite",
                result:
                  labData["urinalysisnitrite"] ||
                  labData["urine_nitrite"] ||
                  labData["nitrite"],
                normal: null,
                unit: getUnitFromMaster("Urine Nitrite"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Nitrite",
                sex,
                age,
              });
            }

            if (
              labData["urinalysisbilirubin"] ||
              labData["urine_bilirubin"] ||
              labData["bilirubin_urine"]
            ) {
              const param = {
                name: "Urine Bilirubin",
                result:
                  labData["urinalysisbilirubin"] ||
                  labData["urine_bilirubin"] ||
                  labData["bilirubin_urine"],
                normal: null,
                unit: getUnitFromMaster("Urine Bilirubin"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Bilirubin",
                sex,
                age,
              });
            }

            if (
              labData["urinalysisurobilinogen"] ||
              labData["urine_urobilinogen"] ||
              labData["urobilinogen"]
            ) {
              const param = {
                name: "Urobilinogen",
                result:
                  labData["urinalysisurobilinogen"] ||
                  labData["urine_urobilinogen"] ||
                  labData["urobilinogen"],
                normal: null,
                unit: getUnitFromMaster("Urobilinogen"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Urobilinogen",
                sex,
                age,
              });
            }

            // Hepatitis Markers
            if (labData["hbsag"] || labData["hbsaghbsagresult"]) {
              const param = {
                name: "HBsAg",
                result: labData["hbsag"] || labData["hbsaghbsagresult"],
                normal: null,
                unit: getUnitFromMaster("HBsAg"),
                isHepatitis: true,
              };
              parameters.push(param);
              testsNeedingNormalRange.push({ param, name: "HBsAg", sex, age });
            }

            if (
              labData["hbsagconcentrationresult"] ||
              labData["hbsaghbsagconcentrationresult"]
            ) {
              const param = {
                name: "HBsAg concentration",
                result:
                  labData["hbsagconcentrationresult"] ||
                  labData["hbsaghbsagconcentrationresult"],
                normal: null,
                unit: getUnitFromMaster("HBsAg concentration"),
                isHepatitis: true,
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "HBsAg concentration",
                sex,
                age,
              });
            }

            if (labData["anti-hbs"] || labData["antihbsantihbsresult"]) {
              const param = {
                name: "HBsAb",
                result: labData["anti-hbs"] || labData["antihbsantihbsresult"],
                normal: null,
                unit: getUnitFromMaster("HBsAb"),
                isHepatitis: true,
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "HBsAb",
                sex,
                age,
              });
            }

            if (
              labData["antihbsantihbsconcen"] ||
              labData["antihbsantihbsconcentrationresult"]
            ) {
              const param = {
                name: "Anti-HBs Concentration",
                result:
                  labData["antihbsantihbsconcen"] ||
                  labData["antihbsantihbsconcentrationresult"],
                normal: null,
                unit: getUnitFromMaster("Anti-HBs Concentration"),
                isHepatitis: true,
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Anti-HBs Concentration",
                sex,
                age,
              });
            }

            if (labData["hcvabhcvabresult"]) {
              const param = {
                name: "HCV Ab",
                result: labData["hcvabhcvabresult"],
                normal: null,
                unit: getUnitFromMaster("HCV Ab"),
                isHepatitis: true,
              };
              parameters.push(param);
              testsNeedingNormalRange.push({ param, name: "HCV Ab", sex, age });
            }

            if (labData["hcvabcoiresult"]) {
              const param = {
                name: "COI",
                result: labData["hcvabcoiresult"],
                normal: null,
                unit: getUnitFromMaster("COI"),
                isHepatitis: true,
              };
              parameters.push(param);
              testsNeedingNormalRange.push({ param, name: "COI", sex, age });
            }

            // Stool Examination
            const translateStoolValue = (val) => {
              if (!val) return val;
              const map = {
                notfound: "ไม่พบ",
                found: "พบ",
                negative: "ไม่พบ",
                positive: "พบ",
                brown: "น้ำตาล",
                lightbrown: "น้ำตาลอ่อน",
                darkbrown: "น้ำตาลเข้ม",
                yellow: "เหลือง",
                green: "เขียว",
                black: "ดำ",
                red: "แดง",
                soft: "นิ่ม",
                hard: "แข็ง",
                liquid: "เหลว",
                formed: "เป็นก้อน",
                semiformed: "กึ่งแข็ง",
                watery: "เหลวมาก",
              };
              return map[val.toLowerCase().replace(/\s+/g, "")] || val;
            };

            if (
              labData["stool_color_result"] ||
              labData["directsmearstoolcolorresult"]
            ) {
              const param = {
                name: "Stool Color",
                result:
                  labData["stool_color_result"] ||
                  labData["directsmearstoolcolorresult"],
                normal: null,
                unit: getUnitFromMaster("Stool Color"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Color (Stool)",
                sex,
                age,
              });
            }

            if (
              labData["stool_rbc_result"] ||
              labData["directsmearstoolrbcresult"]
            ) {
              const param = {
                name: "Stool RBC",
                result:
                  labData["stool_rbc_result"] ||
                  labData["directsmearstoolrbcresult"],
                normal: null,
                unit: getUnitFromMaster("Stool RBC"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Stool RBC",
                sex,
                age,
              });
            }

            if (
              labData["stool_wbc_result"] ||
              labData["directsmearstoolwbcresult"]
            ) {
              const param = {
                name: "Stool WBC",
                result:
                  labData["stool_wbc_result"] ||
                  labData["directsmearstoolwbcresult"],
                normal: null,
                unit: getUnitFromMaster("Stool WBC"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Stool WBC",
                sex,
                age,
              });
            }

            if (labData["stool_parasite_result"] || labData["parasite"]) {
              const param = {
                name: "Parasite",
                result: labData["stool_parasite_result"] || labData["parasite"],
                normal: null,
                unit: getUnitFromMaster("Parasite"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Parasite",
                sex,
                age,
              });
            }

            if (
              labData["stool_consistency_result"] ||
              labData["consistency"] ||
              labData["directsmearstoolconsistencyresult"]
            ) {
              const param = {
                name: "Consistency",
                result:
                  labData["stool_consistency_result"] ||
                  labData["consistency"] ||
                  labData["directsmearstoolconsistencyresult"],
                normal: null,
                unit: getUnitFromMaster("Consistency"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Consistency",
                sex,
                age,
              });
            }

            if (
              labData["basophil_count"] ||
              labData["basophil-count"] ||
              labData["cbcbasophileresult"]
            ) {
              const param = {
                name: "Basophil",
                result:
                  labData["basophil_count"] ||
                  labData["basophil-count"] ||
                  labData["cbcbasophileresult"],
                normal: null,
                unit: getUnitFromMaster("Basophil"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Basophil",
                sex,
                age,
              });
            }

            if (labData["lymphocyte-count"] && !labData["lymphocyte_count"]) {
              const param = {
                name: "Lymphocyte",
                result: labData["lymphocyte-count"],
                normal: null,
                unit: getUnitFromMaster("Lymphocyte"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Lymphocyte",
                sex,
                age,
              });
            }

            if (labData["monocyte-count"] && !labData["monocyte_count"]) {
              const param = {
                name: "Monocyte",
                result: labData["monocyte-count"],
                normal: null,
                unit: getUnitFromMaster("Monocyte"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Monocyte",
                sex,
                age,
              });
            }

            // Physical Examination
            if (labData["bmi"]) {
              const param = {
                name: "BMI",
                result: labData["bmi"],
                normal: null,
                unit: getUnitFromMaster("BMI"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({ param, name: "BMI", sex, age });
            }

            const sysDiasRaw =
              labData["sys/dias"] ||
              record["sys/dias"] ||
              profile["sys/dias"] ||
              "";
            const sysDiasParts =
              sysDiasRaw && sysDiasRaw.includes("/")
                ? sysDiasRaw.split("/").map((v) => v.trim())
                : [];

            const bpsValue =
              labData["bps"] ||
              labData["sys"] ||
              record.bps ||
              record.sys ||
              profile.bps ||
              profile.sys ||
              sysDiasParts[0];
            if (bpsValue) {
              const param = {
                name: "BPS",
                result: String(bpsValue),
                normal: null,
                unit: getUnitFromMaster("BPS"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({ param, name: "BPS", sex, age });
            }

            const bpdValue =
              labData["bpd"] ||
              labData["dia"] ||
              record.bpd ||
              record.dia ||
              profile.bpd ||
              profile.dia ||
              profile.dias ||
              sysDiasParts[1];
            if (bpdValue) {
              const param = {
                name: "BPD",
                result: String(bpdValue),
                normal: null,
                unit: getUnitFromMaster("BPD"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({ param, name: "BPD", sex, age });
            }

            const pulseValue =
              labData["pulse"] || record.pulse || profile.pulse;
            if (pulseValue) {
              const param = {
                name: "Pulse",
                result: String(pulseValue),
                normal: null,
                unit: getUnitFromMaster("Pulse"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({ param, name: "Pulse", sex, age });
            }

            // Urinalysis - Casts
            if (labData["urinalysishyalinecas"]) {
              const { value, unit } = separateValueAndUnit(
                labData["urinalysishyalinecas"],
              );
              const param = {
                name: "Hyaline cast",
                result: value,
                normal: null,
                unit: unit,
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Hyaline cast",
                sex,
                age,
              });
            }

            if (labData["urinalysisgranularca"]) {
              const { value, unit } = separateValueAndUnit(
                labData["urinalysisgranularca"],
              );
              const param = {
                name: "Granular cast",
                result: value,
                normal: null,
                unit: unit,
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Granular cast",
                sex,
                age,
              });
            }

            if (labData["urinalysiscellularca"]) {
              const { value, unit } = separateValueAndUnit(
                labData["urinalysiscellularca"],
              );
              const param = {
                name: "Cellular cast",
                result: value,
                normal: null,
                unit: unit,
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Cellular cast",
                sex,
                age,
              });
            }

            if (labData["urinalysiswaxycastno"]) {
              const { value, unit } = separateValueAndUnit(
                labData["urinalysiswaxycastno"],
              );
              const param = {
                name: "Waxy cast",
                result: value,
                normal: null,
                unit: unit,
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Waxy cast",
                sex,
                age,
              });
            }

            // Urinalysis - Crystals
            if (labData["urinalysiscalciumoxa"]) {
              const param = {
                name: "Calcium oxalate crystal",
                result: labData["urinalysiscalciumoxa"],
                normal: null,
                unit: "",
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Calcium oxalate crystal",
                sex,
                age,
              });
            }

            if (labData["urinalysiscalciumcar"]) {
              const param = {
                name: "Calcium Carbonate crystal",
                result: labData["urinalysiscalciumcar"],
                normal: null,
                unit: "",
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Calcium Carbonate crystal",
                sex,
                age,
              });
            }

            if (labData["urinalysisuricacidcr"]) {
              const param = {
                name: "Uric acid crystal",
                result: labData["urinalysisuricacidcr"],
                normal: null,
                unit: "",
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Uric acid crystal",
                sex,
                age,
              });
            }

            if (labData["urinalysistriplephos"]) {
              const param = {
                name: "Triple phosphate crystal",
                result: labData["urinalysistriplephos"],
                normal: null,
                unit: "",
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Triple phosphate crystal",
                sex,
                age,
              });
            }

            if (
              labData["urinalysiscalciumphos"] ||
              labData["calcium_phosphate"]
            ) {
              const param = {
                name: "Calcium phosphate crystal",
                result:
                  labData["urinalysiscalciumphos"] ||
                  labData["calcium_phosphate"],
                normal: null,
                unit: "",
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Calcium phosphate crystal",
                sex,
                age,
              });
            }

            if (labData["urinalysiscystinecryst"] || labData["cystine"]) {
              const param = {
                name: "Cystine crystal",
                result: labData["urinalysiscystinecryst"] || labData["cystine"],
                normal: null,
                unit: "",
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Cystine crystal",
                sex,
                age,
              });
            }

            if (labData["urinalysisrbccast"] || labData["rbc_cast"]) {
              const { value, unit } = separateValueAndUnit(
                labData["urinalysisrbccast"] || labData["rbc_cast"],
              );
              const param = {
                name: "RBC cast",
                result: value,
                normal: null,
                unit: unit,
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "RBC cast",
                sex,
                age,
              });
            }

            // Electrolytes
            if (
              labData["sodium"] ||
              labData["na"] ||
              labData["electrolytesodiumresult"]
            ) {
              const param = {
                name: "Sodium",
                result:
                  labData["sodium"] ||
                  labData["na"] ||
                  labData["electrolytesodiumresult"],
                normal: null,
                unit: getUnitFromMaster("Sodium"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Sodium",
                sex,
                age,
              });
            }

            if (
              labData["potassium"] ||
              labData["k"] ||
              labData["electrolytepotassiumresult"]
            ) {
              const param = {
                name: "Potassium",
                result:
                  labData["potassium"] ||
                  labData["k"] ||
                  labData["electrolytepotassiumresult"],
                normal: null,
                unit: getUnitFromMaster("Potassium"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Potassium",
                sex,
                age,
              });
            }

            if (
              labData["chloride"] ||
              labData["cl"] ||
              labData["electrolytechorideresult"]
            ) {
              const param = {
                name: "Chloride",
                result:
                  labData["chloride"] ||
                  labData["cl"] ||
                  labData["electrolytechorideresult"],
                normal: null,
                unit: getUnitFromMaster("Chloride"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Chloride",
                sex,
                age,
              });
            }

            if (
              labData["bicarbonate"] ||
              labData["hco3"] ||
              labData["electrolyteco2result"]
            ) {
              const param = {
                name: "Bicarbonate",
                result:
                  labData["bicarbonate"] ||
                  labData["hco3"] ||
                  labData["electrolyteco2result"],
                normal: null,
                unit: getUnitFromMaster("Bicarbonate"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Bicarbonate",
                sex,
                age,
              });
            }

            if (
              labData["calcium"] ||
              labData["ca"] ||
              labData["total_calcium"] ||
              labData["electrolytecalciumresult"]
            ) {
              const param = {
                name: "Total Calcium",
                result:
                  labData["calcium"] ||
                  labData["ca"] ||
                  labData["total_calcium"] ||
                  labData["electrolytecalciumresult"],
                normal: null,
                unit: getUnitFromMaster("Total Calcium"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Total Calcium",
                sex,
                age,
              });
            }

            if (
              labData["phosphate"] ||
              labData["inorganic_phosphate"] ||
              labData["electrolytephosphorusresult"]
            ) {
              const param = {
                name: "Inorganic phosphate",
                result:
                  labData["phosphate"] ||
                  labData["inorganic_phosphate"] ||
                  labData["electrolytephosphorusresult"],
                normal: null,
                unit: getUnitFromMaster("Inorganic phosphate"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Inorganic phosphate",
                sex,
                age,
              });
            }

            if (
              labData["magnesium"] ||
              labData["mg"] ||
              labData["electrolytemagnesiumresult"]
            ) {
              const param = {
                name: "Magnesium",
                result:
                  labData["magnesium"] ||
                  labData["mg"] ||
                  labData["electrolytemagnesiumresult"],
                normal: null,
                unit: getUnitFromMaster("Magnesium"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Magnesium",
                sex,
                age,
              });
            }

            // Tumor Markers
            if (labData["afp"] || labData["tumormarkerafpresult"]) {
              const param = {
                name: "AFP",
                result: labData["afp"] || labData["tumormarkerafpresult"],
                normal: null,
                unit: getUnitFromMaster("AFP"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "AFP",
                sex,
                age,
              });
            }

            if (labData["psa"] || labData["tumormarkerpsaresult"]) {
              const param = {
                name: "PSA",
                result: labData["psa"] || labData["tumormarkerpsaresult"],
                normal: null,
                unit: getUnitFromMaster("PSA"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "PSA",
                sex,
                age,
              });
            }

            if (labData["cea"] || labData["tumormarkercearesult"]) {
              const param = {
                name: "CEA",
                result: labData["cea"] || labData["tumormarkercearesult"],
                normal: null,
                unit: getUnitFromMaster("CEA"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "CEA",
                sex,
                age,
              });
            }

            if (
              labData["ca19_9"] ||
              labData["ca199"] ||
              labData["ca19-9"] ||
              labData["tumormarkerca199result"]
            ) {
              const param = {
                name: "CA19-9",
                result:
                  labData["ca19_9"] ||
                  labData["ca199"] ||
                  labData["ca19-9"] ||
                  labData["tumormarkerca199result"],
                normal: null,
                unit: getUnitFromMaster("CA19-9"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "CA19-9",
                sex,
                age,
              });
            }

            if (labData["ca125"] || labData["tumormarkerca125result"]) {
              const param = {
                name: "CA125",
                result: labData["ca125"] || labData["tumormarkerca125result"],
                normal: null,
                unit: getUnitFromMaster("CA125"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "CA125",
                sex,
                age,
              });
            }

            // Thyroid Hormones
            if (
              labData["free_t3"] ||
              labData["ft3"] ||
              labData["freet3"] ||
              labData["freet3result"]
            ) {
              const param = {
                name: "Free T3",
                result:
                  labData["free_t3"] ||
                  labData["ft3"] ||
                  labData["freet3"] ||
                  labData["freet3result"],
                normal: null,
                unit: getUnitFromMaster("Free T3"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Free T3",
                sex,
                age,
              });
            }

            if (
              labData["free_t4"] ||
              labData["ft4"] ||
              labData["freet4"] ||
              labData["freet4result"]
            ) {
              const param = {
                name: "Free T4",
                result:
                  labData["free_t4"] ||
                  labData["ft4"] ||
                  labData["freet4"] ||
                  labData["freet4result"],
                normal: null,
                unit: getUnitFromMaster("Free T4"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Free T4",
                sex,
                age,
              });
            }

            if (labData["tsh"] || labData["tshresult"]) {
              const param = {
                name: "TSH",
                result: labData["tsh"] || labData["tshresult"],
                normal: null,
                unit: getUnitFromMaster("TSH"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "TSH",
                sex,
                age,
              });
            }

            // Other Tests
            if (
              labData["cholinesterase"] ||
              labData["totalcholinesteraseresult"]
            ) {
              const param = {
                name: "Cholinesterase",
                result:
                  labData["cholinesterase"] ||
                  labData["totalcholinesteraseresult"],
                normal: null,
                unit: getUnitFromMaster("Cholinesterase"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Cholinesterase",
                sex,
                age,
              });
            }

            if (labData["eosinophil_count"] || labData["eosinophil-count"]) {
              const param = {
                name: "Eosinophil",
                result:
                  labData["eosinophil_count"] || labData["eosinophil-count"],
                normal: null,
                unit: getUnitFromMaster("Eosinophil"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Eosinophil",
                sex,
                age,
              });
            }

            if (testsNeedingNormalRange.length > 0 && masterData) {
              testsNeedingNormalRange.forEach((test) => {
                const normalRange = getNormalRangeFromMaster(
                  test.name,
                  test.sex,
                  test.age,
                );
                if (test.param) {
                  test.param.normal = normalRange;
                  test.param.result = normalizeResultValue(
                    test.param.result,
                    normalRange,
                  );
                }
              });
            }

            parameters.forEach((param) => {
              if (!param.normal) {
                param.normal = "-";
              }
              if (!param.unit) {
                param.unit = "";
              }
            });

            const hideParamNames = new Set();
            if (masterData) {
              for (const categoryData of Object.values(masterData)) {
                for (const key of Object.keys(categoryData)) {
                  if (key.startsWith("_hide_")) {
                    hideParamNames.add(
                      key.replace(/^_hide_/, "").toLowerCase(),
                    );
                  }
                }
              }
            }

            const filteredParameters = parameters.filter((param) => {
              const normalLower = (param.normal || "")
                .toString()
                .toLowerCase()
                .trim();
              const resultLower = (param.result || "")
                .toString()
                .toLowerCase()
                .trim();
              const normalizedName = normalizeParameterName(
                param.name,
              ).toLowerCase();

              if (
                hideParamNames.has(normalizedName) &&
                normalLower === "negative" &&
                resultLower === "negative"
              ) {
                return false;
              }

              return true;
            });

            const finalParameters = filteredParameters;

            const formatDate = (dateString) => {
              if (!dateString || dateString === "-") return "-";
              const datePart = dateString.split(" ")[0];
              const parts = datePart.split("-");
              if (parts.length === 3) {
                return `${parts[2]}-${parts[1]}-${parts[0]}`;
              }
              return dateString;
            };

            let approvalStatus = "pending";
            let approvedAt = null;
            let approvedBy = null;
            let nutritionAdvice = "";
            let exerciseAdvice = "";
            let sleepAdvice = "";
            let doctorAdvice = "";
            let dbRecordId = null;

            const visitDateFull = record.dateadd || null;
            try {
              const healthRecordResponse =
                await healthRecordsAPI.getRecordIdByIdentifier(
                  record.memberid,
                  visitDateFull,
                );
              if (
                healthRecordResponse.status === "success" &&
                healthRecordResponse.data?.record_id
              ) {
                dbRecordId = healthRecordResponse.data.record_id;
              }
            } catch (err) {
              // No health record found yet
            }

            // ดึงข้อมูล recommendations — เรียกเฉพาะเมื่อมี dbRecordId เพื่อไม่ให้ผูก approval ของ visit อื่น
            if (dbRecordId) {
              try {
                const recResponse =
                  await healthRecommendationAPI.getDraft(dbRecordId);
                if (recResponse.status === "success" && recResponse.data) {
                  approvalStatus =
                    recResponse.data.approval_status || "pending";
                  approvedAt = recResponse.data.approved_at;
                  approvedBy = recResponse.data.approved_by || null;
                  nutritionAdvice = recResponse.data.nutrition_advice || "";
                  exerciseAdvice = recResponse.data.exercise_advice || "";
                  sleepAdvice = recResponse.data.sleep_advice || "";
                  doctorAdvice = recResponse.data.doctor_advice || "";
                }
              } catch (err) {
                // No recommendations yet for this member
              }
            }

            return {
              id: `${record.memberid}_${record.dateadd}`,
              memberid: record.memberid,
              dbRecordId: dbRecordId,
              no: index + 1,
              date: formatDate(record.dateadd),
              rawDate: record.dateadd || "",
              patientName:
                `${profile.nameprefix || ""} ${profile.name || ""} ${
                  profile.lastname || ""
                }`.trim() || "-",
              gender: profile.gender === "1" ? "ชาย" : "หญิง",
              age: age || "-",
              diseases: profile.congenitaldisease || "ไม่มี",
              company: record.campaignname || labData["company"] || "-",
              campaign: record.campaignname || labData["company"] || "-",
              foodAllergies:
                labData["food_allergies"] || labData["food allergies"] || "-",
              hn: record.memberid || "-",
              nid: profile.nid || profile.idCard || null,
              parameters: finalParameters,
              templates: [],
              matchedTemplates: [],
              tags: [],
              approvalStatus: approvalStatus,
              approvedAt: approvedAt,
              approvedBy: approvedBy,
              isApproved: approvalStatus === "approved",
              labData: labData,
              weight: labData["weight"] || "-",
              height: labData["height"] || "-",
              bmi: labData["bmi"] || "-",
              bloodPressure:
                labData["sys"] && labData["dia"]
                  ? `${labData["sys"]}/${labData["dia"]}`
                  : labData["sys/dias"] || "-",
              pulse: labData["pulse"] || "-",
              doctorNote: doctorAdvice || labData["doctornote"] || "-",
              nutritionAdvice: nutritionAdvice,
              exerciseAdvice: exerciseAdvice,
              sleepAdvice: sleepAdvice,
            };
          }),
        );

      // ✅ ดึง getLabList หน้าแรก
      const firstLabRes = await memberAPI.getLabList({ ...apiFilters, pageNow: 1 });
      const labPageAmount = firstLabRes.pageAmount || 1;

      // ✅ รอ templates ก่อน (โหลด parallel กับ page 1 อยู่แล้ว ใช้เวลาน้อย)
      await templatesPromise;

      // ✅ Process page 1 ทันที — match templates พร้อมกันเลย
      const page1Records = deduplicateList(
        firstLabRes.result && Array.isArray(firstLabRes.list) ? firstLabRes.list : [],
      );
      if (page1Records.length > 0) {
        const page1Formatted = await formatBatch(page1Records);
        allFormattedData = allFormattedData.concat(page1Formatted);
        await matchTemplatesWithRecords(page1Formatted, allTemplates);
        sortAndUpdate(allFormattedData);
        if (currentFetchId === fetchIdRef.current) setLoading(false);
      }

      // ✅ ดึงหน้าที่เหลือของ lab ทีละหน้า — match templates ทันทีต่อ batch
      for (let page = 2; page <= labPageAmount; page++) {
        if (currentFetchId !== fetchIdRef.current) return;
        try {
          const res = await memberAPI.getLabList({ ...apiFilters, pageNow: page });
          if (res.result && Array.isArray(res.list)) {
            const newRecords = deduplicateList(res.list);
            if (newRecords.length > 0) {
              const pageFormatted = await formatBatch(newRecords);
              allFormattedData = allFormattedData.concat(pageFormatted);
              await matchTemplatesWithRecords(pageFormatted, allTemplates);
              sortAndUpdate(allFormattedData);
              if (currentFetchId === fetchIdRef.current) setLoading(false);
            }
          }
        } catch (err) {
          console.error(`❌ Error fetching lab page ${page}:`, err);
        }
      }

      // ✅ Final sort (template match ทำไปแล้วทุก batch แล้ว)
      if (allFormattedData.length > 0) {
        sortAndUpdate(allFormattedData);
      } else {
        if (currentFetchId !== fetchIdRef.current) return;
        setHealthRecords([]);
        setAllHealthRecords([]);
      }
    } catch (err) {
      console.error("Error fetching health records:", err);
      if (currentFetchId === fetchIdRef.current) {
        setHealthRecords([]);
        setAllHealthRecords([]);
      }
    } finally {
      if (currentFetchId === fetchIdRef.current) {
        setLoading(false);
        // โหลดครบแล้ว — apply search text ที่รออยู่ (ถ้ามี) พรึ่บทีเดียว
        setIsFetchingPages(false);
        if (pendingSearchTextRef.current !== null) {
          const pending = pendingSearchTextRef.current;
          pendingSearchTextRef.current = null;
          activeSearchTextRef.current = pending;
          setActiveSearchText(pending);
        }
      }
    }
  };

  return (
    <Layout>
      <DatePickerStyles />
      <PageContainer>
        <PageTitle>สมุดสุขภาพ</PageTitle>

        <FilterSection>
          <GridItem column="1" row="1">
            <Label>วันที่ตรวจเริ่ม</Label>
          </GridItem>
          <GridItem column="2" row="1">
            <Label>ถึง</Label>
          </GridItem>
          <GridItem column="3" row="1">
            <Label>ค้นหาชื่อ-สกุล, HN</Label>
          </GridItem>
          <GridItem column="4" row="1">
            <Label>บริษัท</Label>
          </GridItem>
          <GridItem column="5" row="1">
            <Label>สถานะ</Label>
          </GridItem>

          {/* แถวที่ 2 - Input Fields และ Buttons */}
          <GridItem column="1" row="2">
            <DateInputContainer>
              <DatePicker
                selected={startDate}
                onChange={(date) => setStartDate(date)}
                dateFormat="dd/MM/yyyy"
                placeholderText="เลือกวันที่"
                showYearDropdown
                showMonthDropdown
                dropdownMode="select"
                customInput={<DateInput />}
              />
              <CalendarIcon>
                <Calendar size={16} />
              </CalendarIcon>
            </DateInputContainer>
          </GridItem>

          <GridItem column="2" row="2">
            <DateInputContainer>
              <DatePicker
                selected={endDate}
                onChange={(date) => setEndDate(date)}
                dateFormat="dd/MM/yyyy"
                placeholderText="เลือกวันที่"
                showYearDropdown
                showMonthDropdown
                dropdownMode="select"
                customInput={<DateInput />}
              />
              <CalendarIcon>
                <Calendar size={16} />
              </CalendarIcon>
            </DateInputContainer>
          </GridItem>

          <GridItem column="3" row="2">
            <TextInput
              type="text"
              placeholder="พิมพ์ชื่อหรือ HN"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </GridItem>

          <GridItem column="4" row="2">
            <SelectInput
              value={searchCompany}
              onChange={handleCompanyChange}
            >
              <option value="">ทั้งหมด</option>
              {companies
                .filter((company) => company.name && company.name !== "-")
                .map((company) => (
                  <option key={company.id} value={company.name}>
                    {company.name}
                  </option>
                ))}
            </SelectInput>
          </GridItem>

          <GridItem column="5" row="2">
            <SelectInput
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="">เลือกสถานะ</option>
              <option value="รอการอนุมัติ">รอการอนุมัติ</option>
              <option value="อนุมัติและจัดส่งผลการตรวจสุขภาพแล้ว">
                อนุมัติและจัดส่งผลการตรวจสุขภาพแล้ว
              </option>
              <option value="มีการแก้ไขรอการอนุมัติอีกครั้ง">
                มีการแก้ไขรอการอนุมัติอีกครั้ง
              </option>
            </SelectInput>
          </GridItem>

          <GridItem column="6" row="2">
            <SearchButton onClick={handleSearch}>ค้นหา</SearchButton>
          </GridItem>

          <GridItem column="7" row="2">
            <PrintButton onClick={handleBatchPrint}>
              <Printer size={16} />
              Print
            </PrintButton>
          </GridItem>
        </FilterSection>

        {loading ? (
          <LoadingContainer>
            <LoadingSpinner />
            <LoadingText>กำลังโหลด...</LoadingText>
          </LoadingContainer>
        ) : (
          <TableContainer>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell rowSpan="2" style={{ width: "50px" }}>
                    <Checkbox
                      onChange={handleSelectAll}
                      checked={
                        healthRecords.length > 0 &&
                        healthRecords
                          .slice(
                            (currentPage - 1) * itemsPerPage,
                            currentPage * itemsPerPage,
                          )
                          .every((record) =>
                            selectedRecords.includes(record.id),
                          )
                      }
                    />
                  </TableHeaderCell>
                  <TableHeaderCell rowSpan="2" style={{ width: "50px" }}>
                    ที่
                  </TableHeaderCell>
                  <TableHeaderCell rowSpan="2" style={{ width: "100px" }}>
                    วันที่ตรวจ
                  </TableHeaderCell>
                  <TableHeaderCell rowSpan="2" style={{ width: "180px" }}>
                    ชื่อผู้รับการตรวจ/เพศ/อายุ/โรคประจำตัว
                  </TableHeaderCell>
                  <TableHeaderCell rowSpan="2" style={{ width: "120px" }}>
                    HN
                  </TableHeaderCell>
                  <TableHeaderCell colSpan="4">ผลการตรวจสุขภาพ</TableHeaderCell>
                  <TableHeaderCell rowSpan="2" style={{ width: "200px" }}>
                    การแปลผลสุขภาพที่กำหนดไว้เบื้องต้น/ Doctor Note
                  </TableHeaderCell>
                  <TableHeaderCell rowSpan="2" style={{ width: "150px" }}>
                    การจัดการข้อมูล
                  </TableHeaderCell>
                </TableRow>
                <TableRow>
                  <TableHeaderCell2 style={{ width: "150px" }}>
                    พารามิเตอร์
                  </TableHeaderCell2>
                  <TableHeaderCell2 style={{ width: "150px" }}>
                    ค่าปกติ
                  </TableHeaderCell2>
                  <TableHeaderCell2 style={{ width: "120px" }}>
                    ค่าที่ตรวจวัดได้
                  </TableHeaderCell2>
                  <TableHeaderCell2
                    style={{ width: "80px" }}
                  ></TableHeaderCell2>
                </TableRow>
              </TableHeader>
              <tbody>
                {healthRecords
                  .slice(
                    (currentPage - 1) * itemsPerPage,
                    currentPage * itemsPerPage,
                  )
                  .map((record, rowIndex) => (
                    <TableRow key={record.no}>
                      <TableCell>
                        <Checkbox
                          checked={selectedRecords.includes(record.id)}
                          onChange={() => handleCheckboxChange(record.id)}
                        />
                      </TableCell>
                      <TableCell>
                        {(currentPage - 1) * itemsPerPage + rowIndex + 1}
                      </TableCell>
                      <TableCell>
                        <div>{record.date}</div>
                        {record.rawDate && (
                          <div
                            style={{
                              fontSize: "14px",
                              color: "#6B7280",
                              marginTop: "2px",
                            }}
                          >
                            {record.rawDate.split(" ")[1]?.slice(0, 8) || ""}
                          </div>
                        )}
                      </TableCell>
                      <TableCell style={{ textAlign: "left" }}>
                        <div>
                          <span style={{ fontWeight: "700" }}>ชื่อ:</span>{" "}
                          <span style={{ fontWeight: "400" }}>
                            {record.patientName}
                          </span>
                        </div>
                        <div>
                          <span style={{ fontWeight: "700" }}>เพศ:</span>{" "}
                          {record.gender}
                        </div>
                        <div>
                          <span style={{ fontWeight: "700" }}>อายุ:</span>{" "}
                          {record.age} ปี
                        </div>
                        <div>
                          <span style={{ fontWeight: "700" }}>
                            โรคประจำตัว:
                          </span>
                        </div>
                        <div style={{ fontWeight: "400" }}>
                          {record.diseases}
                        </div>
                        <div>
                          <span style={{ fontWeight: "700" }}>บริษัท:</span>{" "}
                          <span style={{ fontWeight: "400" }}>
                            {record.company}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>{record.hn}</TableCell>
                      <TableCell colSpan="4" style={{ padding: 0 }}>
                        <table
                          style={{ width: "100%", borderCollapse: "collapse" }}
                        >
                          <tbody>
                            {(() => {
                              const categorized = categorizeParameters(
                                record.parameters,
                              );
                              return Object.entries(categorized).map(
                                ([category, params], catIndex) => (
                                  <React.Fragment key={`cat-${catIndex}`}>
                                    {/* Category Header */}
                                    <tr>
                                      <td
                                        colSpan="4"
                                        style={{
                                          padding: "0.5rem 0 0.25rem 0",
                                          fontWeight: "700",
                                          fontSize: "13px",
                                          color: "#4a4a4a",
                                          textAlign: "left",
                                          borderTop:
                                            catIndex > 0
                                              ? "1px solid #e0e0e0"
                                              : "none",
                                        }}
                                      >
                                        {category}
                                      </td>
                                    </tr>
                                    {/* Parameters in this category */}
                                    {params.map((param, index) => {
                                      const rawComparison = compareValues(
                                        param.normal,
                                        param.result,
                                      );
                                      const isTextCategory =
                                        category === "ผลการตรวจปัสสาวะ" ||
                                        category === "ผลการตรวจอุจจาระ";
                                      const comparison =
                                        param.isHepatitis &&
                                        rawComparison.label !== "-"
                                          ? {
                                              status:
                                                rawComparison.status ===
                                                "normal"
                                                  ? "normal"
                                                  : "high",
                                              label:
                                                rawComparison.status ===
                                                "normal"
                                                  ? "Negative"
                                                  : "Positive",
                                            }
                                          : isTextCategory &&
                                              rawComparison.label !== "-" &&
                                              rawComparison.status !== "normal"
                                            ? {
                                                status: "high",
                                                label: "Abnormal",
                                              }
                                            : rawComparison;
                                      return (
                                        <tr key={`${catIndex}-${index}`}>
                                          <td
                                            style={{
                                              padding: "0.125rem 1rem",
                                              textAlign: "left",
                                              fontSize: "12px",
                                              color: "#6F6F6F",
                                              width: "150px",
                                            }}
                                          >
                                            {param.name}
                                          </td>
                                          <td
                                            style={{
                                              padding: "0.125rem 1rem",
                                              textAlign: "center",
                                              fontSize: "12px",
                                              color: "#6F6F6F",
                                              whiteSpace: "nowrap",
                                              width: "150px",
                                            }}
                                          >
                                            {param.normal}
                                          </td>
                                          <td
                                            style={{
                                              padding: "0.125rem 0.5rem",
                                              textAlign: "center",
                                              fontSize: "12px",
                                              color: "#6F6F6F",
                                              width: "120px",
                                            }}
                                          >
                                            {param.result}
                                          </td>
                                          <td
                                            style={{
                                              padding: "0.125rem 0.25rem",
                                              textAlign: "left",
                                              fontSize: "12px",
                                              color: "#6F6F6F",
                                              width: "80px",
                                            }}
                                          >
                                            <ComparisonBadge
                                              status={comparison.status}
                                            >
                                              {comparison.label}
                                            </ComparisonBadge>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </React.Fragment>
                                ),
                              );
                            })()}
                          </tbody>
                        </table>
                      </TableCell>
                      <TableCell style={{ textAlign: "left" }}>
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "0.25rem",
                            marginBottom: "0.5rem",
                          }}
                        >
                          {record.templates.map((templateName, index) => {
                            const matchedTemplate =
                              record.matchedTemplates?.find(
                                (t) => t.template_name === templateName,
                              );

                            return (
                              <TemplateBadge
                                key={index}
                                disabled={record.isApproved}
                                onClick={() =>
                                  handleTemplateClick(
                                    templateName,
                                    matchedTemplate,
                                  )
                                }
                              >
                                <TemplateBadgeText>
                                  {templateName}
                                </TemplateBadgeText>
                              </TemplateBadge>
                            );
                          })}
                        </div>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "0.25rem",
                          }}
                        >
                          {(record.approvalStatus === "approved" ||
                            record.approvalStatus === "revised") && (
                            <HistoryButton
                              onClick={() => handleViewHistory(record.id)}
                            >
                              <History />
                              ประวัติการส่งผลสุขภาพ
                            </HistoryButton>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "0.5rem",
                            alignItems: "center",
                          }}
                        >
                          <ViewDataButton
                            onClick={() => handleViewData(record.id)}
                          >
                            ดูข้อมูล
                          </ViewDataButton>
                          <StatusText>สถานะจัดส่ง</StatusText>
                          <StatusBadge
                            status={
                              record.approvalStatus === "approved"
                                ? "อนุมัติและจัดส่งผลการตรวจสุขภาพแล้ว"
                                : record.approvalStatus === "revised"
                                  ? "มีการแก้ไขรอการอนุมัติอีกครั้ง"
                                  : "รอการอนุมัติ"
                            }
                          >
                            {record.approvalStatus === "approved"
                              ? "อนุมัติและจัดส่งผลการตรวจสุขภาพแล้ว"
                              : record.approvalStatus === "revised"
                                ? "มีการแก้ไขรอการอนุมัติอีกครั้ง"
                                : "รอการอนุมัติ"}
                          </StatusBadge>
                          <ActionPrintButton
                            onClick={() => handlePrint(record.id)}
                          >
                            <Printer size={14} />
                            Print
                          </ActionPrintButton>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                {!isFetchingPages && !loading && healthRecords.length === 0 && (
                  <TableRow>
                    <TableCell colSpan="20" style={{ textAlign: "center", color: "#9ca3af", padding: "2rem" }}>
                      ไม่พบรายการ
                    </TableCell>
                  </TableRow>
                )}
              </tbody>
            </Table>

            {/* Pagination */}
            {healthRecords.length > 0 && (
              <PaginationContainer>
                <PaginationInfo>
                  แสดง {(currentPage - 1) * itemsPerPage + 1} ถึง{" "}
                  {Math.min(currentPage * itemsPerPage, healthRecords.length)}{" "}
                  จาก {healthRecords.length} รายการ
                </PaginationInfo>
                <PaginationControls>
                  <PageButton
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(1, prev - 1))
                    }
                    disabled={currentPage === 1}
                  >
                    ก่อนหน้า
                  </PageButton>

                  {(() => {
                    const totalPages = Math.ceil(
                      healthRecords.length / itemsPerPage,
                    );
                    const pages = [];

                    if (totalPages <= 7) {
                      for (let i = 1; i <= totalPages; i++) {
                        pages.push(
                          <PageButton
                            key={i}
                            active={currentPage === i}
                            onClick={() => setCurrentPage(i)}
                          >
                            {i}
                          </PageButton>,
                        );
                      }
                    } else {
                      pages.push(
                        <PageButton
                          key={1}
                          active={currentPage === 1}
                          onClick={() => setCurrentPage(1)}
                        >
                          1
                        </PageButton>,
                      );

                      if (currentPage > 3) {
                        pages.push(
                          <PageEllipsis key="ellipsis1">...</PageEllipsis>,
                        );
                      }

                      const start = Math.max(2, currentPage - 1);
                      const end = Math.min(totalPages - 1, currentPage + 1);

                      for (let i = start; i <= end; i++) {
                        pages.push(
                          <PageButton
                            key={i}
                            active={currentPage === i}
                            onClick={() => setCurrentPage(i)}
                          >
                            {i}
                          </PageButton>,
                        );
                      }

                      if (currentPage < totalPages - 2) {
                        pages.push(
                          <PageEllipsis key="ellipsis2">...</PageEllipsis>,
                        );
                      }

                      pages.push(
                        <PageButton
                          key={totalPages}
                          active={currentPage === totalPages}
                          onClick={() => setCurrentPage(totalPages)}
                        >
                          {totalPages}
                        </PageButton>,
                      );
                    }

                    return pages;
                  })()}

                  <PageButton
                    onClick={() =>
                      setCurrentPage((prev) =>
                        Math.min(
                          Math.ceil(healthRecords.length / itemsPerPage),
                          prev + 1,
                        ),
                      )
                    }
                    disabled={
                      currentPage ===
                      Math.ceil(healthRecords.length / itemsPerPage)
                    }
                  >
                    ถัดไป
                  </PageButton>
                </PaginationControls>
              </PaginationContainer>
            )}
          </TableContainer>
        )}
      </PageContainer>

      <TemplateDetailModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        templateData={selectedTemplate}
      />

      <EditTemplateModal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        recordId={selectedRecordId}
        selectedRecord={selectedRecord}
        onSave={() => {
          const currentFilters = {};
          if (startDate) currentFilters.dateStart = formatDateForAPI(startDate);
          if (endDate)
            currentFilters.dateStop = formatDateStopToYYYYMMDD(endDate);
          fetchHealthRecords(currentFilters, { preservePage: true });
        }}
      />

      <HealthReportModal
        isOpen={isHealthReportModalOpen}
        onClose={handleCloseHealthReportModal}
        recordData={selectedRecord}
      />

      {/* Hidden Templates Modal */}
      {isHiddenTemplatesModalOpen && (
        <HiddenTemplatesModal
          isOpen={isHiddenTemplatesModalOpen}
          onClose={() => setIsHiddenTemplatesModalOpen(false)}
          hiddenTemplates={hiddenTemplates}
          onRestore={handleRestoreTemplate}
        />
      )}

      {/* Alert Modal */}
      {isAlertModalOpen && (
        <AlertModalOverlay onClick={() => setIsAlertModalOpen(false)}>
          <AlertModalContainer onClick={(e) => e.stopPropagation()}>
            <AlertModalBody>
              กรุณาเลือกผู้รับการตรวจที่ต้องการ Print
            </AlertModalBody>
            <AlertModalButton onClick={() => setIsAlertModalOpen(false)}>
              ตกลง
            </AlertModalButton>
          </AlertModalContainer>
        </AlertModalOverlay>
      )}

      <HealthRecordsHistory
        isOpen={isHistoryModalOpen}
        onClose={() => setIsHistoryModalOpen(false)}
        selectedRecord={historySelectedRecord}
      />
    </Layout>
  );
};

const HiddenTemplatesModal = ({
  isOpen,
  onClose,
  hiddenTemplates,
  onRestore,
}) => {
  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Templates ที่ซ่อนไว้</ModalTitle>
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </ModalHeader>
        <ModalBody>
          {hiddenTemplates.length === 0 ? (
            <EmptyMessage>ไม่มี Templates ที่ซ่อนไว้</EmptyMessage>
          ) : (
            <HiddenTemplatesList>
              {hiddenTemplates.map((template) => (
                <HiddenTemplateItem key={template.template_id}>
                  <TemplateInfo>
                    <TemplateName>{template.template_name}</TemplateName>
                    <TemplateDetails>
                      <div>
                        ซ่อนเมื่อ:{" "}
                        {new Date(template.hidden_at).toLocaleString("th-TH")}
                      </div>
                      {template.tags && template.tags.length > 0 && (
                        <TagsContainer>
                          {template.tags.map((tag, index) => (
                            <Tag key={index}>{tag}</Tag>
                          ))}
                        </TagsContainer>
                      )}
                    </TemplateDetails>
                  </TemplateInfo>
                  <RestoreButton
                    onClick={() => onRestore(template.template_id)}
                  >
                    เอากลับมา
                  </RestoreButton>
                </HiddenTemplateItem>
              ))}
            </HiddenTemplatesList>
          )}
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  );
};

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 0.5rem;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
`;

const ModalTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #6b7280;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #111827;
  }
`;

const ModalBody = styled.div`
  padding: 1.5rem;
  overflow-y: auto;
`;

const EmptyMessage = styled.div`
  text-align: center;
  color: #6b7280;
  padding: 2rem;
`;

const HiddenTemplatesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const HiddenTemplateItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  background-color: #f9fafb;
`;

const TemplateInfo = styled.div`
  flex: 1;
`;

const TemplateName = styled.div`
  font-weight: 600;
  color: #111827;
  margin-bottom: 0.5rem;
`;

const TemplateDetails = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin-top: 0.5rem;
`;

const Tag = styled.span`
  background-color: #e0f2f1;
  color: #00796b;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
`;

const RestoreButton = styled.button`
  background-color: #00b5b8;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #009fa2;
  }
`;

const AlertModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`;

const AlertModalContainer = styled.div`
  background: white;
  border-radius: 0.5rem;
  width: 450px;
  max-width: 90vw;
  padding: 3rem 2rem;
  box-shadow:
    0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
`;

const AlertModalBody = styled.div`
  color: #111827;
  font-size: 1.25rem;
  font-weight: 500;
  text-align: center;
  line-height: 1.6;
`;

const AlertModalButton = styled.button`
  background-color: #00b5b8;
  color: white;
  border: none;
  padding: 0.75rem 2rem;
  border-radius: 0.375rem;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  min-width: 100px;

  &:hover {
    background-color: #009fa2;
  }
`;

export default HealthBook;
